import './styles.css';
console.log('[LifeDashboard] CSS imported');
import { DashboardCore } from './dashboard-core.js';
import { DashboardAuth } from './dashboard-auth.js';
import { DashboardSchedule } from './dashboard-schedule.js';
import { DashboardUI } from './dashboard-ui.js';
import { DashboardFocus } from './dashboard-focus.js';
import { SimpleBrainDump } from './components/simple-brain-dump.js';
import { EnoughCapacity } from './components/enough-capacity.js';
import { ProjectsTable } from './components/projects-table.js';
import { TaskIntegrationHub } from './services/task-integration-hub.js';
import { GoogleOAuthClient } from './services/google-oauth-client.js';
import { GoogleCalendarService } from './services/google-calendar-service.js';
import { ResponsiveLayoutController } from './components/responsive-layout-controller.js';
import { GuidedDailyFlow } from './components/guided-daily-flow.js';
import { ValuesAnchors } from './components/values-anchors.js';
import { MobileLayoutManager } from './components/mobile-layout-manager.js';
import { ThemeController } from './utils/theme-controller.js';

/**
 * Main App Controller
 * Orchestrates all dashboard modules and provides global app state
 */
export class LifeDashboard {
  constructor() {
    // Initialize core module
    this.core = new DashboardCore();
    this.modules = {};

    // Initialize debug configuration
    this.debug = {
      headerToggle: false,
    };

    // Initialize authentication module
    this.auth = new DashboardAuth(this);

    // Initialize schedule module
    this.schedule = new DashboardSchedule(this.core);

    // Initialize UI module
    this.ui = new DashboardUI(this);

    // Initialize focus mode module
    this.focus = new DashboardFocus(this);

    // Initialize UI module (handles header, welcome section, etc.)
    this.ui.initialize();

    // OAuth and calendar services
    this.oauthClient = null;
    this.calendarService = null;
    this.oauthPopup = null;
    this.handleOAuthWindowMessage = this.handleOAuthWindowMessage.bind(this);

    // Additional components
    this.mobileLayoutManager = null;

    // Initialize asynchronously
    this.init().catch(error => {
      console.error('[LifeDashboard] Initialization failed:', error);
    });
  }

  bootstrapDefaultData() {
    const defaults = {
      userPreferences: {
        structureIntensity: 'medium',
        visualDensity: 'balanced',
        sensoryMode: 'visual',
      },
      layoutPreferences: {
        responsiveLayout: true,
      },
      headerCollapsed: true,
      integrationState: {
        calendar: {
          connected: false,
          status: 'disabled',
          lastSync: null,
          error: null,
        },
        tasks: {
          lastEventType: null,
          lastUpdatedAt: null,
        },
      },
      calendarWriteConsent: {
        enabled: false,
        grantedAt: null,
      },
      valuesAnchors: {
        lifeVest: 'Pause · breathe · remind myself this dashboard is a safe porch to land on.',
        northStar: 'Build a sustainable rhythm that supports coaching, creativity, and rest.',
        anchor: 'Morning meds, hydration, lo-fi playlist, gentle body check-in every noon.',
        buoy: 'Name one small win + send a voice note to Future Me before closing laptop.',
        updatedAt: null,
      },
      sleepSettings: this.sleepSettingsDefaults,
      leaveSchedule: this.leaveScheduleDefaults,
      countdowns: this.cloneCountdownArray(this.countdownDefaults),
      _analyticsCounters: {},
      _analyticsEvents: [],
    };

    if (typeof this.core.data.ensureDefaults === 'function') {
      this.core.data.ensureDefaults(defaults);
      return;
    }

    Object.entries(defaults).forEach(([key, value]) => {
      if (this.core.data.get(key) == null) {
        this.core.data.set(key, value);
      }
    });
  }

  async init() {
    this.loadDebugConfiguration();

    // Initialize authentication overlay and account controls
    const forceLocalMode = import.meta?.env?.VITE_FORCE_LOCAL_MODE === 'true';
    console.log(
      `[LifeDashboard] Starting in ${forceLocalMode ? 'local (forced)' : 'cloud-preferred'} mode`
    );

    // Initialize theme controller first
    this.themeController = new ThemeController();

    // Initialize task integration hub
    this.taskHub = new TaskIntegrationHub(this.core.data, {
      featureFlags: this.core.featureFlags,
      updateIntegrationState: (namespace, patch) => this.updateIntegrationState(namespace, patch),
    });

    // Initialize authentication module
    await this.auth.initialize();

    // Check if focus mode is enabled
    const focusModeEnabled = this.core.featureFlags.isEnabled('focusMode');

    if (focusModeEnabled) {
      // Initialize focus mode (sidebar + panels)
      await this.focus.initialize();
    } else {
      // Initialize traditional dashboard layout
      // Initialize priority section components (wireframe top section)
      this.initializePriorityComponents();

      // Initialize secondary section components
      this.initializeSecondaryComponents();

      // Initialize guided daily flow coach
      this.initializeGuidedFlow();

      // Initialize tertiary ADHD-optimized modules
      this.initializeTertiaryComponents();

      // Initialize mobile layout experience
      this.initializeMobileLayout();
    }

    // Create placeholder content for remaining modules
    this.renderADHDPlaceholders();

    // Attach global event listeners
    this.attachGlobalEvents();

    // Initialize sidebar toggle
    this.initializeSidebarToggle();

    // Initialize settings menu
    this.initializeSettingsMenu();

    // Make globally available for component interactions
    window.lifeDashboard = this;
    window.lifeAnalytics = this.core.analytics;
    window.lifeDashboardDebug = {
      setHeaderToggleDebug: (enabled) => this.setHeaderToggleDebug(enabled),
      toggleHeaderToggleDebug: () => this.setHeaderToggleDebug(!this.core.debug.headerToggle),
      logHeaderToggleState: () => this.ui.logHeaderToggleState(),
    };

    // Initialize responsive layout controller
    this.initializeResponsiveLayout();

    // Initialize Google services when enabled
    this.initializeGoogleIntegrations();

    console.log('Life Dashboard v5 initialized with Wireframe Priority Layout');
  }

  track(eventName, payload = {}) {
    this.core.analytics?.track(eventName, payload);
  }

  createLeaveEntry(label = '', leaveTime = '08:30', buffer = 15, days = []) {
    return this.core.createLeaveEntry(label, leaveTime, buffer, days);
  }

  generateLeaveEntryId() {
    return this.core.generateLeaveEntryId();
  }

  escapeHtml(value) {
    return this.core.escapeHtml(value);
  }

  handleAuthStateChange(user) {
    this.auth.handleAuthStateChange(user);
  }

  handleLogout() {
    this.auth.handleLogout();
  }

  cloneCountdownArray(list) {
    return this.core.cloneCountdownArray(list);
  }

  cloneCountdown(countdown) {
    return this.core.cloneCountdown(countdown);
  }

  buildCountdownDefaults() {
    const nowIso = new Date().toISOString();
    const workSchedule = this.normalizeLeaveSchedule(this.leaveScheduleDefaults);
    const sleepSettings = this.normalizeSleepSettings(this.sleepSettingsDefaults);

    return [
      {
        id: this.countdownIds?.work ?? 'system-work',
        label: 'Leave for work',
        source: 'system',
        category: 'work',
        mode: 'leaveBy',
        offsetMinutes: 0,
        schedule: workSchedule,
        target: null,
        calendarEventId: null,
        active: true,
        metadata: { system: true },
        createdAt: nowIso,
        updatedAt: nowIso,
      },
      {
        id: this.countdownIds?.sleep ?? 'system-sleep',
        label: 'Wind down',
        source: 'system',
        category: 'sleep',
        mode: 'leaveBy',
        offsetMinutes: sleepSettings.windDownMinutes ?? 60,
        settings: sleepSettings,
        target: null,
        calendarEventId: null,
        active: true,
        metadata: { system: true },
        createdAt: nowIso,
        updatedAt: nowIso,
      },
      {
        id: this.countdownIds?.appointment ?? 'system-next-appointment',
        label: 'Next appointment',
        source: 'system',
        category: 'appointment',
        mode: 'eventStart',
        offsetMinutes: 0,
        target: null,
        calendarEventId: null,
        active: true,
        metadata: { system: true, autoAttach: true },
        createdAt: nowIso,
        updatedAt: nowIso,
      },
    ];
  }

  initializeCountdownState() {
    const defaultMap = new Map(
      this.countdownDefaults.map((item) => [item.id, this.normalizeCountdown(item)])
    );

    const persistedCountdowns = this.normalizeCountdownCollection(
      this.data.get('countdowns', [])
    );
    const legacyCustomCountdowns = this.convertLegacyCustomCountdowns(
      this.data.get('customCountdowns', [])
    );

    const collection = [...persistedCountdowns, ...legacyCustomCountdowns];
    const byId = new Map();
    collection.forEach((item) => {
      if (!item?.id) {
        return;
      }
      const existing = byId.get(item.id);
      if (existing) {
        byId.set(item.id, this.normalizeCountdown({ ...existing, ...item }));
      } else {
        byId.set(item.id, this.normalizeCountdown(item));
      }
    });

    const legacyLeaveSchedule = this.normalizeLeaveSchedule(
      this.data.get('leaveSchedule', this.leaveScheduleDefaults)
    );
    const legacySleepSettings = this.normalizeSleepSettings(
      this.data.get('sleepSettings', this.sleepSettingsDefaults)
    );

    const pickSchedule = (...candidates) => {
      for (const candidate of candidates) {
        const normalized = this.normalizeLeaveSchedule(candidate);
        if (normalized?.entries?.length) {
          return normalized;
        }
      }
      return this.normalizeLeaveSchedule(this.leaveScheduleDefaults);
    };

    const pickSleepSettings = (...candidates) => {
      for (const candidate of candidates) {
        const normalized = this.normalizeSleepSettings(candidate);
        if (normalized?.configured || normalized) {
          return normalized;
        }
      }
      return this.normalizeSleepSettings(this.sleepSettingsDefaults);
    };

    const ensureSystemCountdown = (id, overrides = {}) => {
      const base = defaultMap.get(id);
      const current = byId.get(id);
      const merged = this.normalizeCountdown({
        ...base,
        ...current,
        ...overrides,
      });
      if (merged) {
        byId.set(id, merged);
      }
    };

    ensureSystemCountdown(this.countdownIds?.work, {
      schedule: pickSchedule(byId.get(this.countdownIds?.work)?.schedule, legacyLeaveSchedule),
    });
    ensureSystemCountdown(this.countdownIds?.sleep, {
      settings: pickSleepSettings(byId.get(this.countdownIds?.sleep)?.settings, legacySleepSettings),
      offsetMinutes:
        pickSleepSettings(byId.get(this.countdownIds?.sleep)?.settings, legacySleepSettings)
          .windDownMinutes ?? 0,
    });
    ensureSystemCountdown(this.countdownIds?.appointment);

    const normalizedCountdowns = Array.from(byId.values()).filter(Boolean);
    this.countdowns = normalizedCountdowns;

    this.data.set('countdowns', this.cloneCountdownArray(normalizedCountdowns));

    const workCountdown = byId.get(this.countdownIds?.work);
    const sleepCountdown = byId.get(this.countdownIds?.sleep);

    this.leaveSchedule = this.normalizeLeaveSchedule(workCountdown?.schedule);
    this.sleepSettings = this.normalizeSleepSettings(sleepCountdown?.settings);

    this.data.set('leaveSchedule', this.leaveSchedule);
    this.data.set('sleepSettings', this.sleepSettings);

    this.refreshCountdownStack();
  }

  normalizeCountdownCollection(list) {
    if (!Array.isArray(list)) {
      return [];
    }
    return list
      .map((item) => this.normalizeCountdown(item))
      .filter((item) => Boolean(item));
  }

  normalizeCountdown(rawCountdown) {
    if (!rawCountdown || typeof rawCountdown !== 'object') {
      return null;
    }

    const base = rawCountdown;
    const id =
      typeof base.id === 'string' && base.id.trim()
        ? base.id.trim()
        : `countdown_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    const label = typeof base.label === 'string' && base.label.trim() ? base.label.trim() : 'Countdown';

    let mode = base.mode;
    if (mode === 'event') mode = 'eventStart';
    if (mode === 'leave') mode = 'leaveBy';
    if (mode !== 'eventStart' && mode !== 'leaveBy') {
      mode = 'eventStart';
    }

    let source = base.source;
    if (!['manual', 'calendar', 'system'].includes(source)) {
      source = id.startsWith('system-')
        ? 'system'
        : base.calendarEventId
        ? 'calendar'
        : 'manual';
    }

    let category = base.category || base.type || 'custom';
    if (category === 'appointment' || category === 'custom' || category === 'sleep' || category === 'work') {
      // keep provided category
    } else if (source === 'system') {
      category = this.inferCountdownCategory(id);
    } else if (source === 'calendar') {
      category = 'appointment';
    } else {
      category = 'custom';
    }

    let offsetMinutes = Number.parseInt(base.offsetMinutes, 10);
    if (!Number.isFinite(offsetMinutes) || offsetMinutes < 0) {
      offsetMinutes = 0;
    }

    const target =
      typeof base.target === 'string' && base.target
        ? base.target
        : typeof base.targetDateTime === 'string'
        ? base.targetDateTime
        : null;

    const calendarEventId =
      typeof base.calendarEventId === 'string' && base.calendarEventId.trim()
        ? base.calendarEventId.trim()
        : null;

    const metadata =
      base.metadata && typeof base.metadata === 'object' && base.metadata !== null
        ? { ...base.metadata }
        : {};

    let schedule = null;
    if (base.schedule) {
      schedule = this.normalizeLeaveSchedule(base.schedule);
    }

    let settings = null;
    if (base.settings) {
      settings = this.normalizeSleepSettings(base.settings);
    }

    const active = base.active !== false;

    return {
      id,
      label,
      source,
      category,
      type: category === 'custom' ? 'custom' : category,
      mode,
      offsetMinutes,
      target,
      calendarEventId,
      metadata,
      schedule,
      settings,
      active,
      createdAt: base.createdAt || metadata.createdAt || null,
      updatedAt: base.updatedAt || metadata.updatedAt || null,
    };
  }

  normalizeSleepSettings(rawSettings = {}) {
    const base = typeof rawSettings === 'object' && rawSettings !== null ? rawSettings : {};
    const bedtime = /^\d{2}:\d{2}$/.test(String(base.bedtime))
      ? String(base.bedtime)
      : this.sleepSettingsDefaults.bedtime;
    let windDownMinutes = Number.parseInt(base.windDownMinutes, 10);
    if (!Number.isFinite(windDownMinutes) || windDownMinutes < 0) {
      windDownMinutes = this.sleepSettingsDefaults.windDownMinutes;
    }
    const configured = Boolean(base.configured);
    return {
      bedtime,
      windDownMinutes,
      configured,
    };
  }

  convertLegacyCustomCountdowns(list) {
    if (!Array.isArray(list)) {
      return [];
    }
    return list
      .map((item) => {
        if (!item) return null;
        const mapped = {
          ...item,
          target: item.targetDateTime || item.target || null,
          source: item.source || (item.calendarEventId ? 'calendar' : 'manual'),
          mode: item.mode || 'eventStart',
          offsetMinutes: Number.isFinite(Number(item.offsetMinutes))
            ? Number(item.offsetMinutes)
            : Number.isFinite(Number(item.buffer))
            ? Number(item.buffer)
            : 0,
          category: item.type === 'appointment' ? 'appointment' : 'custom',
        };
        return this.normalizeCountdown(mapped);
      })
      .filter(Boolean);
  }

  inferCountdownCategory(id) {
    if (typeof id !== 'string') {
      return 'custom';
    }
    if (id.includes('work')) return 'work';
    if (id.includes('sleep') || id.includes('bed')) return 'sleep';
    if (id.includes('appointment')) return 'appointment';
    return 'custom';
  }

  getCountdownById(id) {
    if (!id) {
      return null;
    }
    return (this.countdowns || []).find((item) => item.id === id) || null;
  }

  persistCountdowns(nextCountdowns) {
    this.countdowns = this.normalizeCountdownCollection(nextCountdowns);
    this.data.set('countdowns', this.cloneCountdownArray(this.countdowns));

    const workCountdown = this.getCountdownById(this.countdownIds?.work);
    const sleepCountdown = this.getCountdownById(this.countdownIds?.sleep);

    if (workCountdown?.schedule) {
      this.leaveSchedule = this.normalizeLeaveSchedule(workCountdown.schedule);
      this.data.set('leaveSchedule', this.leaveSchedule);
    }

    if (sleepCountdown?.settings) {
      this.sleepSettings = this.normalizeSleepSettings(sleepCountdown.settings);
      this.data.set('sleepSettings', this.sleepSettings);
    }
  }

  updateCountdown(id, updater) {
    const list = [...(this.countdowns || [])];
    const index = list.findIndex((item) => item.id === id);
    if (index === -1) {
      return;
    }
    const current = list[index];
    const next = this.normalizeCountdown({
      ...current,
      ...(typeof updater === 'function' ? updater(current) : updater),
    });
    list[index] = next;
    this.persistCountdowns(list);
  }

  addCountdown(countdown) {
    const normalized = this.normalizeCountdown(countdown);
    if (!normalized) {
      return null;
    }
    const list = [...(this.countdowns || []), normalized];
    this.persistCountdowns(list);
    return normalized;
  }

  removeCountdown(id) {
    if (!id) return;
    const list = (this.countdowns || []).filter((item) => item.id !== id);
    this.persistCountdowns(list);
  }

  updateSettingsUserInfo(user) {
    const userInfoDiv = document.getElementById('settings-user-info');
    const greetingSpan = document.getElementById('user-greeting');
    const emailSpan = document.getElementById('user-email');

    if (userInfoDiv && greetingSpan && emailSpan) {
      if (user) {
        userInfoDiv.style.display = 'block';
        const displayName = user.displayName;
        greetingSpan.textContent = displayName ? `Hi, ${displayName}!` : 'Hi 👋';
        emailSpan.textContent = user.email ?? 'Signed in';
      } else {
        userInfoDiv.style.display = 'none';
      }
    }
  }

  initializePriorityComponents() {
    // UI module handles welcome section and header initialization

    // Initialize projects table
    const projectsTableContainer = document.getElementById('projects-table-container');
    if (projectsTableContainer) {
      this.modules.projectsTable = new ProjectsTable(
        projectsTableContainer,
        this.data,
        this.taskHub
      );
    }
  }

  initializeSecondaryComponents() {
    // Initialize Focus Timer
    this.modules.focusTimer = new FocusTimer(
      document.getElementById('focus-timer-container'),
      this.data
    );

    const countdownContainer = document.getElementById('countdown-stack-container');
    if (countdownContainer) {
      this.modules.countdownStack = new CountdownStack(countdownContainer, this.data, {
        analytics: this.analytics,
        getBuiltinEntries: () => this.composeCountdownStackEntries(),
        getCalendarEvents: () => this.data.get('calendar_events', []),
        onRefreshRequested: () => this.refreshCountdownStack(),
      });

      if (!this.countdownRefreshInterval && typeof window !== 'undefined') {
        this.countdownRefreshInterval = window.setInterval(
          () => this.refreshCountdownStack(),
          30_000
        );
      }
    }

    // Initialize Enough Capacity in secondary position
    this.modules.enoughCapacity = new EnoughCapacity(
      document.getElementById('enough-capacity-container'),
      this.data,
      this.taskHub
    );

    // Initialize Brain Dump in secondary position
    this.modules.simpleBrainDump = new SimpleBrainDump(
      document.getElementById('brain-dump-plus-container'),
      this.data,
      this.taskHub
    );

    // Initialize Weekly Calendar
    this.modules.weeklyCalendar = new WeeklyCalendar(
      document.getElementById('weekly-calendar-container'),
      this.data
    );
  }


  /**
   * Initialize only the header components (focus timer, weekly calendar)
   * Used in Focus Mode to populate the welcome panels without initializing
   * the traditional dashboard components
   */
  initializeHeaderComponents() {
    // Initialize Focus Timer (in welcome panels)
    this.modules.focusTimer = new FocusTimer(
      document.getElementById('focus-timer-container'),
      this.data
    );

    // Initialize Weekly Calendar (in welcome panels)
    this.modules.weeklyCalendar = new WeeklyCalendar(
      document.getElementById('weekly-calendar-container'),
      this.data
    );
  }

  async initializeFocusMode() {
    console.log('[LifeDashboard] Initializing focus mode...');

    // Focus mode always begins with a collapsed header footprint
    this.data.set('headerCollapsed', true);
    if (typeof document !== 'undefined' && document.body) {
      document.body.dataset.header = 'collapsed';
    }

    // Initialize welcome section (greeting, clock, countdown) - needed for header
    // UI module handles header initialization

    try {
      const { initializeFocusMode } = await import('./features/focus-mode-integration.js');

      this.focusMode = await initializeFocusMode({
        data: this.data,
        taskHub: this.taskHub,
        modules: this.modules
      });

      this.ensureFocusWorkspaceListener();
      this.applyFocusModeInitialState();

      if (this.focusMode?.disable) {
        const originalDisable = this.focusMode.disable;
        this.focusMode.disable = async (...args) => {
          if (this.focusWorkspaceListenerAttached) {
            document.removeEventListener('workspace-changed', this.handleFocusWorkspaceChange);
            this.focusWorkspaceListenerAttached = false;
          }
          if (typeof document !== 'undefined' && document.body) {
            delete document.body.dataset.focusPanels;
            delete document.body.dataset.focusLayout;
          }
          return originalDisable.apply(this.focusMode, args);
        };
      }

      console.log('[LifeDashboard] Focus mode initialized successfully!');
    } catch (error) {
      console.error('[LifeDashboard] Failed to initialize focus mode:', error);
    }
  }

  ensureFocusWorkspaceListener() {
    if (this.focusWorkspaceListenerAttached) {
      return;
    }

    document.addEventListener('workspace-changed', this.handleFocusWorkspaceChange);
    this.focusWorkspaceListenerAttached = true;
  }

  applyFocusModeInitialState() {
    const workspace = this.focusMode?.workspace;
    const activePanels = Array.isArray(workspace?.activePanels) ? workspace.activePanels : [];
    this.focusWorkspaceState.lastPanelCount = activePanels.length;

    let layout = 'layout-empty';
    if (workspace && typeof workspace.getLayoutClass === 'function') {
      layout = workspace.getLayoutClass();
    } else if (activePanels.length === 1) {
      layout = 'layout-single';
    } else if (activePanels.length === 2) {
      layout = 'layout-split-2';
    } else if (activePanels.length >= 3) {
      layout = 'layout-split-3';
    }

    this.updateFocusWorkspaceMetadata(activePanels.length, layout);

    // When authenticated, start with expanded header to show only header
    // When not authenticated, use collapsed header for focus mode
    const shouldCollapse = !document.body.dataset.auth;
    this.setHeaderCollapsed(shouldCollapse);
  }

  updateFocusWorkspaceMetadata(panelCount = 0, layout = 'layout-empty') {
    if (typeof document === 'undefined' || !document.body) {
      return;
    }

    const normalizedCount = Number.isFinite(panelCount) ? panelCount : 0;
    document.body.dataset.focusPanels = String(normalizedCount);

    if (layout) {
      document.body.dataset.focusLayout = layout;
    } else {
      delete document.body.dataset.focusLayout;
    }
  }

  handleFocusWorkspaceChange(event) {
    const detail = event?.detail ?? {};
    const activePanels = Array.isArray(detail.activePanels) ? detail.activePanels : [];
    const panelCount = activePanels.length;
    const previousCount = this.focusWorkspaceState?.lastPanelCount ?? 0;

    this.focusWorkspaceState.lastPanelCount = panelCount;

    const layout =
      detail.layout ||
      (panelCount === 0
        ? 'layout-empty'
        : typeof this.focusMode?.workspace?.getLayoutClass === 'function'
          ? this.focusMode.workspace.getLayoutClass()
          : panelCount === 1
            ? 'layout-single'
            : panelCount === 2
              ? 'layout-split-2'
              : 'layout-split-3');

    this.updateFocusWorkspaceMetadata(panelCount, layout);

    if (panelCount > 0 && previousCount === 0) {
      // When authenticated, keep header expanded; otherwise collapse for focus mode
      const shouldCollapse = !document.body.dataset.auth;
      this.setHeaderCollapsed(shouldCollapse);
    } else if (panelCount === 0 && previousCount > 0) {
      // When authenticated, keep header expanded; otherwise collapse for focus mode
      const shouldCollapse = !document.body.dataset.auth;
      this.setHeaderCollapsed(shouldCollapse);
    }
  }

  initializeGuidedFlow() {
    const container = document.getElementById('guided-flow-container');
    if (!container) {
      console.warn('[LifeDashboard] Guided flow container not found');
      return;
    }

    this.modules.guidedFlow = new GuidedDailyFlow(container, this.data, {
      modules: this.modules,
      onOpenPlanner: () => {
        const plannerBtn = document.getElementById('enough-open-planner');
        if (plannerBtn) {
          plannerBtn.click();
        } else {
          this.modules.enoughCapacity?.render?.();
        }
      },
      analytics: this.analytics,
    });
  }

  initializeTertiaryComponents() {
    const anchorsContainer = document.getElementById('values-anchors-container');
    if (anchorsContainer) {
      this.modules.valuesAnchors = new ValuesAnchors(anchorsContainer, this.data, {
        analytics: this.analytics,
      });
    }
  }

  initializeMobileLayout() {
    if (this.mobileLayoutManager) {
      this.mobileLayoutManager.modules = this.modules;
      return;
    }

    const mainDashboard = document.querySelector('.main-dashboard');
    if (!mainDashboard) {
      console.warn('[LifeDashboard] Main dashboard not found for mobile layout initialization');
      return;
    }

    this.mobileLayoutManager = new MobileLayoutManager(mainDashboard, {
      dataManager: this.data,
      modules: this.modules,
      taskHub: this.taskHub,
    });
  }

  initializeWelcomeSection() {
    // Initialize greeting
    this.updateGreeting();

    // Initialize and start real-time clock
    this.updateClock();
    this.clockInterval = setInterval(() => this.updateClock(), 1000);

    // UI module handles calendar and header initialization

    // Initialize leave-for-work tools
    this.initializeLeaveForWorkTools();
  }

  updateGreeting() {
    const greetingContainer = document.getElementById('greeting-container');
    if (greetingContainer) {
      const now = new Date();
      const hour = now.getHours();
      let greeting = 'Good morning';
      let subtitle = 'Ready to make today amazing?';

      if (hour >= 12 && hour < 17) {
        greeting = 'Good afternoon';
        subtitle = "How's your day going?";
      } else if (hour >= 17) {
        greeting = 'Good evening';
        subtitle = 'Time to wind down and reflect';
      }

      // Add energy-based encouragement for ADHD
      const encouragements = [
        "You've got this! 💪",
        'One step at a time',
        'Your pace is perfect',
        'Progress over perfection',
        'Be kind to yourself today',
        'Small wins count too',
      ];

      if (Math.random() > 0.5) {
        subtitle = encouragements[Math.floor(Math.random() * encouragements.length)];
      }

      const name = 'Marlie';

      greetingContainer.innerHTML = `
        <h2>${greeting}, ${name}!</h2>
        <p class="greeting-subtitle">${subtitle}</p>
      `;
    }
  }

  updateClock() {
    const now = new Date();

    // Update time
    const timeContainer = document.getElementById('current-time');
    if (timeContainer) {
      timeContainer.textContent = now.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    }

    // Update date
    const dateContainer = document.getElementById('current-date');
    if (dateContainer) {
      dateContainer.textContent = now.toLocaleDateString([], {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    }

    const todayKey = now.toDateString();
    if (this.lastCompactCalendarDate !== todayKey) {
      this.renderCompactCalendars();
    }

    this.updateHeaderCountdown(now);

    if (now.getSeconds() % 30 === 0) {
      this.refreshCountdownStack();
    }
  }

  refreshCountdownStack() {
    this.modules.countdownStack?.refresh?.();
    this.updateHeaderCountdown();
  }

  renderCompactCalendars() {
    const today = new Date();
    this.renderCompactMonth(today);
    this.renderCompactWeek(today);
    this.lastCompactCalendarDate = today.toDateString();
  }



  initializeLeaveForWorkTools() {
    this.headerCountdownEl = document.getElementById('header-countdown');
    this.headerCountdownLabelEl = document.getElementById('header-countdown-label');
    this.headerCountdownValueEl = document.getElementById('header-countdown-value');
    this.headerCountdownMetaEl = document.getElementById('header-countdown-meta');
    this.headerCountdownActionEl = document.getElementById('header-leave-schedule-button');

    this.leaveModalEl = document.getElementById('leave-schedule-modal');
    this.leaveFormEl = document.getElementById('leave-schedule-form');
    this.leaveEntryList = document.getElementById('leave-entry-list');
    this.addLeaveEntryButton = document.getElementById('add-leave-entry-button');

    if (this.leaveModalEl) {
      this.leaveModalEl.addEventListener('click', (event) => {
        if (event.target?.dataset?.leaveAction === 'close') {
          event.preventDefault();
          this.closeLeaveScheduleModal();
        }
      });

      this.leaveModalEl.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.closeLeaveScheduleModal();
        }
      });
    }

    if (this.leaveFormEl) {
      this.leaveFormEl.addEventListener('submit', (event) => {
        event.preventDefault();
        this.handleLeaveScheduleSubmit();
      });
    }

    if (this.leaveEntryList) {
      this.leaveEntryList.addEventListener('click', (event) => this.handleLeaveEntryClick(event));
    }

    if (this.addLeaveEntryButton) {
      this.addLeaveEntryButton.addEventListener('click', () => this.addLeaveEntryRow());
    }

    if (typeof this.core.data.subscribe === 'function') {
      this.core.data.subscribe('countdowns', () => {
        const workCountdown = this.getCountdownById(this.countdownIds?.work);
        this.leaveSchedule = this.normalizeLeaveSchedule(workCountdown?.schedule);
        this.populateLeaveScheduleForm();
        this.updateHeaderCountdown();
      });
    }

    this.populateLeaveScheduleForm();
    this.updateHeaderCountdown();
  }

  normalizeLeaveSchedule(rawSchedule = {}) {
    const inputEntries = Array.isArray(rawSchedule?.entries)
      ? [...rawSchedule.entries]
      : [];

    if (!inputEntries.length && rawSchedule?.leaveTime) {
      inputEntries.push({
        id: rawSchedule.id,
        label: rawSchedule.label ?? '',
        leaveTime: rawSchedule.leaveTime,
        buffer: rawSchedule.buffer,
        days: Array.isArray(rawSchedule.workDays) ? rawSchedule.workDays : [],
      });
    }

    const normalizedEntries = inputEntries
      .map((entry) => this.normalizeLeaveEntry(entry))
      .filter((entry) => entry.days.length > 0 && /^\d{2}:\d{2}$/.test(entry.leaveTime));

    if (!normalizedEntries.length && Array.isArray(this.leaveScheduleDefaults.entries)) {
      normalizedEntries.push(
        ...this.leaveScheduleDefaults.entries.map((entry) => this.normalizeLeaveEntry(entry))
      );
    }

    const configured = Boolean(rawSchedule?.configured) && normalizedEntries.length > 0;

    return {
      entries: normalizedEntries,
      configured,
    };
  }

  normalizeLeaveEntry(entry) {
    const base = typeof entry === 'object' && entry !== null ? entry : {};
    const id = base.id || this.generateLeaveEntryId();
    const leaveTime = /^\d{2}:\d{2}$/.test(String(base.leaveTime))
      ? String(base.leaveTime)
      : '08:30';
    let buffer = Number.parseInt(base.buffer, 10);
    buffer = Number.isFinite(buffer) ? Math.min(Math.max(buffer, 0), 180) : 15;
    const days = Array.isArray(base.days)
      ? Array.from(new Set(base.days.filter((day) => this.leaveDayOrder.includes(day))))
      : [];

    return {
      id,
      label: (base.label ?? '').trim(),
      leaveTime,
      buffer,
      days,
    };
  }

  populateLeaveScheduleForm() {
    if (!this.leaveFormEl) {
      return;
    }

    this.activeModalEntries =
      this.leaveSchedule?.entries?.map((entry) => this.normalizeLeaveEntry(entry)) ??
      this.leaveScheduleDefaults.entries.map((entry) => this.normalizeLeaveEntry(entry));

    this.renderLeaveEntries();
  }

  renderLeaveEntries() {
    if (!this.leaveEntryList) {
      return;
    }

    this.leaveEntryList.innerHTML = '';
    const fragment = document.createDocumentFragment();

    const total = this.activeModalEntries.length;
    this.activeModalEntries.forEach((entry) => {
      fragment.appendChild(this.buildLeaveEntryRow(entry, total));
    });

    this.leaveEntryList.appendChild(fragment);
  }

  buildLeaveEntryRow(entry, totalCount = 1) {
    const row = document.createElement('div');
    row.className = 'leave-entry';
    row.dataset.entryId = entry.id;

    const headerRow = document.createElement('div');
    headerRow.className = 'leave-entry-row';

    const labelField = document.createElement('label');
    labelField.className = 'leave-entry-field leave-entry-label';
    labelField.innerHTML = `<span>Label (optional)</span>`;
    const labelInput = document.createElement('input');
    labelInput.type = 'text';
    labelInput.placeholder = 'Job or shift name';
    labelInput.value = entry.label ?? '';
    labelField.appendChild(labelInput);

    const timeField = document.createElement('label');
    timeField.className = 'leave-entry-field leave-entry-time';
    timeField.innerHTML = `<span>Leave time</span>`;
    const timeInput = document.createElement('input');
    timeInput.type = 'time';
    timeInput.required = true;
    timeInput.value = entry.leaveTime ?? '08:30';
    timeField.appendChild(timeInput);

    const bufferField = document.createElement('label');
    bufferField.className = 'leave-entry-field leave-entry-buffer';
    bufferField.innerHTML = `<span>Buffer (min)</span>`;
    const bufferInput = document.createElement('input');
    bufferInput.type = 'number';
    bufferInput.min = '0';
    bufferInput.max = '180';
    bufferInput.step = '5';
    bufferInput.value = Number.isFinite(entry.buffer) ? String(entry.buffer) : '15';
    bufferField.appendChild(bufferInput);

    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.className = 'leave-entry-remove';
    removeButton.dataset.action = 'remove-entry';
    removeButton.textContent = 'Remove';
    if (totalCount <= 1) {
      removeButton.disabled = true;
      removeButton.classList.add('is-disabled');
      removeButton.title = 'At least one schedule is required';
    }

    headerRow.appendChild(labelField);
    headerRow.appendChild(timeField);
    headerRow.appendChild(bufferField);
    headerRow.appendChild(removeButton);

    const dayGroup = document.createElement('div');
    dayGroup.className = 'leave-entry-days';
    dayGroup.setAttribute('role', 'group');
    dayGroup.setAttribute('aria-label', 'Work days');

    this.leaveDayOrder.forEach((dayCode) => {
      const meta = this.leaveDayMeta[dayCode];
      const dayButton = document.createElement('button');
      dayButton.type = 'button';
      dayButton.className = 'leave-day-chip';
      dayButton.dataset.day = dayCode;
      dayButton.dataset.action = 'toggle-day';
      dayButton.textContent = meta.short;
      const isSelected = entry.days.includes(dayCode);
      dayButton.classList.toggle('is-selected', isSelected);
      dayButton.setAttribute('aria-pressed', String(isSelected));
      dayButton.title = meta.label;
      dayGroup.appendChild(dayButton);
    });

    row.appendChild(headerRow);
    row.appendChild(dayGroup);

    return row;
  }

  handleLeaveEntryClick(event) {
    const actionTarget = event.target.closest('[data-action]');
    if (!actionTarget) {
      return;
    }

    const entryElement = actionTarget.closest('.leave-entry');
    if (!entryElement) {
      return;
    }

    const entryId = entryElement.dataset.entryId;
    if (!entryId) {
      return;
    }

    switch (actionTarget.dataset.action) {
      case 'toggle-day':
        event.preventDefault();
        this.syncEntryFormState();
        this.toggleEntryDay(entryId, actionTarget.dataset.day, actionTarget);
        break;
      case 'remove-entry':
        event.preventDefault();
        this.syncEntryFormState();
        this.removeLeaveEntry(entryId);
        break;
      default:
        break;
    }
  }

  addLeaveEntryRow() {
    this.syncEntryFormState();
    this.activeModalEntries.push(
      this.createLeaveEntry('', this.leaveScheduleDefaults.entries[0]?.leaveTime ?? '08:30', 15, [])
    );
    this.renderLeaveEntries();
  }

  removeLeaveEntry(entryId) {
    if (!this.activeModalEntries) {
      return;
    }

    this.activeModalEntries = this.activeModalEntries.filter((entry) => entry.id !== entryId);

    if (!this.activeModalEntries.length) {
      this.activeModalEntries.push(
        this.createLeaveEntry('', this.leaveScheduleDefaults.entries[0]?.leaveTime ?? '08:30', 15, [])
      );
    }

    this.renderLeaveEntries();
  }

  toggleEntryDay(entryId, dayCode, buttonElement) {
    if (!this.leaveDayOrder.includes(dayCode)) {
      return;
    }

    const entry = this.activeModalEntries?.find((item) => item.id === entryId);
    if (!entry) {
      return;
    }

    const dayIndex = entry.days.indexOf(dayCode);
    if (dayIndex >= 0) {
      entry.days.splice(dayIndex, 1);
    } else {
      entry.days.push(dayCode);
      entry.days = Array.from(new Set(entry.days));
    }

    if (buttonElement) {
      const isSelected = entry.days.includes(dayCode);
      buttonElement.classList.toggle('is-selected', isSelected);
      buttonElement.setAttribute('aria-pressed', String(isSelected));
    }
  }

  syncEntryFormState() {
    if (!this.leaveEntryList || !this.activeModalEntries) {
      return;
    }

    const updated = [];
    this.leaveEntryList.querySelectorAll('.leave-entry').forEach((row) => {
      const entryId = row.dataset.entryId || this.generateLeaveEntryId();
      const labelValue =
        row.querySelector('.leave-entry-label input')?.value.trim() ?? '';
      const timeValue = row.querySelector('.leave-entry-time input')?.value ?? '08:30';
      const bufferValue = Number.parseInt(
        row.querySelector('.leave-entry-buffer input')?.value ?? '15',
        10
      );
      const days = Array.from(row.querySelectorAll('.leave-day-chip.is-selected')).map(
        (button) => button.dataset.day
      );

      updated.push({
        id: entryId,
        label: labelValue,
        leaveTime: timeValue,
        buffer: bufferValue,
        days,
      });
    });

    this.activeModalEntries = updated.map((entry) => this.normalizeLeaveEntry(entry));
  }

  openLeaveScheduleModal() {
    if (!this.leaveModalEl) {
      return;
    }

    this.populateLeaveScheduleForm();
    this.leaveModalPreviousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.leaveModalEl.removeAttribute('hidden');
    this.leaveModalEl.classList.add('is-visible');
    document.body.classList.add('leave-modal-open');
    const firstInput =
      this.leaveModalEl.querySelector('.leave-entry input') ??
      this.leaveModalEl.querySelector('button, input, select');
    firstInput?.focus();
  }

  closeLeaveScheduleModal() {
    if (!this.leaveModalEl) {
      return;
    }

    this.leaveModalEl.classList.remove('is-visible');
    this.leaveModalEl.setAttribute('hidden', '');
    document.body.classList.remove('leave-modal-open');

    if (this.leaveModalPreviousFocus?.focus) {
      this.leaveModalPreviousFocus.focus();
    }
    this.leaveModalPreviousFocus = null;
  }

  handleLeaveScheduleSubmit() {
    this.syncEntryFormState();

    const sanitizedEntries = this.activeModalEntries
      ?.map((entry) => this.normalizeLeaveEntry(entry))
      .filter((entry) => entry.days.length > 0 && /^\d{2}:\d{2}$/.test(entry.leaveTime));

    if (!sanitizedEntries || sanitizedEntries.length === 0) {
      this.showNotification('Add at least one leave time and choose the days it applies to.');
      return;
    }

    const updatedSchedule = {
      entries: sanitizedEntries,
      configured: true,
    };

    const updatedAt = new Date().toISOString();
    this.updateCountdown(this.countdownIds?.work, (current) => ({
      schedule: updatedSchedule,
      metadata: { ...(current?.metadata || {}), updatedAt },
      updatedAt,
    }));
    this.closeLeaveScheduleModal();
    this.showNotification('Leave-for-work countdowns updated. We’ll keep you on track!');
  }

  updateLeaveCountdown() {
    this.updateHeaderCountdown();
  }

  updateHeaderCountdown(now = new Date()) {
    if (
      !this.headerCountdownEl ||
      !this.headerCountdownLabelEl ||
      !this.headerCountdownValueEl
    ) {
      return;
    }

    const statusClasses = [
      'header-countdown--warning',
      'header-countdown--critical',
      'header-countdown--empty',
    ];
    this.headerCountdownEl.classList.remove(...statusClasses);

    const entry = this.buildWorkCountdownEntry(now);

    if (!entry) {
      this.headerCountdownEl.classList.add('header-countdown--empty');
      this.headerCountdownLabelEl.textContent = 'Leave for work';
      this.headerCountdownValueEl.textContent = '--';
      if (this.headerCountdownMetaEl) {
        this.headerCountdownMetaEl.textContent = '';
        this.headerCountdownMetaEl.style.display = 'none';
      }
      if (this.headerCountdownActionEl) {
        this.headerCountdownActionEl.textContent = 'Adjust';
        this.headerCountdownActionEl.style.display = '';
        this.headerCountdownActionEl.disabled = false;
      }
      return;
    }

    this.headerCountdownLabelEl.textContent = entry.label || 'Leave for work';
    this.headerCountdownValueEl.textContent = entry.primaryText || '--';

    if (this.headerCountdownMetaEl) {
      if (entry.secondaryText) {
        this.headerCountdownMetaEl.textContent = entry.secondaryText;
        this.headerCountdownMetaEl.style.display = '';
      } else {
        this.headerCountdownMetaEl.textContent = '';
        this.headerCountdownMetaEl.style.display = 'none';
      }
    }

    if (this.headerCountdownActionEl) {
      const canWrite = this.isCalendarWriteReady() && entry?.nextSlot && entry.nextSlot.diffMs > 60_000;
      const actionLabel = canWrite ? 'Block commute' : (entry.primaryActionLabel || 'Adjust');
      this.headerCountdownActionEl.textContent = actionLabel;
      this.headerCountdownActionEl.style.display = '';
      this.headerCountdownActionEl.disabled = false;
      this.headerCountdownActionEl.onclick = canWrite
        ? () => this.createLeaveCalendarBlock(entry.nextSlot)
        : () => this.openLeaveScheduleModal();
    }

    if (entry.status === 'warning') {
      this.headerCountdownEl.classList.add('header-countdown--warning');
    } else if (entry.status === 'critical') {
      this.headerCountdownEl.classList.add('header-countdown--critical');
    }

    if (!entry.configured) {
      this.headerCountdownEl.classList.add('header-countdown--empty');
      this.headerCountdownValueEl.textContent = 'Set schedule';
      if (this.headerCountdownMetaEl) {
        const message = entry.secondaryText || 'Tap Adjust to add your leave times';
        this.headerCountdownMetaEl.textContent = message;
        this.headerCountdownMetaEl.style.display = '';
      }
    }
  }

  computeLeaveScheduleSlots(now = new Date()) {
    const entries = Array.isArray(this.leaveSchedule?.entries) ? this.leaveSchedule.entries : [];
    const upcomingCandidates = [];
    let pastToday = null;

    entries.forEach((entry) => {
      const timeParts = this.parseLeaveTime(entry.leaveTime);
      if (!timeParts) {
        return;
      }
      const [hours, minutes] = timeParts;

      for (let offset = 0; offset <= 14; offset += 1) {
        const dayIndex = (now.getDay() + offset) % 7;
        const dayCode = this.leaveDayOrder[dayIndex];
        if (!entry.days.includes(dayCode)) {
          continue;
        }

        const candidate = new Date(now);
        candidate.setHours(hours, minutes, 0, 0);
        candidate.setDate(now.getDate() + offset);
        const diffMs = candidate.getTime() - now.getTime();

        if (offset === 0 && diffMs < 0) {
          if (!pastToday || diffMs > pastToday.diffMs) {
            pastToday = { entry, date: candidate, diffMs, offsetDays: offset, dayCode };
          }
          continue;
        }

        if (diffMs >= 0) {
          upcomingCandidates.push({ entry, date: candidate, diffMs, offsetDays: offset, dayCode });
          break;
        }
      }
    });

    if (!upcomingCandidates.length) {
      return { upcoming: null, pastToday };
    }

    upcomingCandidates.sort((a, b) => a.diffMs - b.diffMs);
    return { upcoming: upcomingCandidates[0], pastToday };
  }

  parseLeaveTime(timeString) {
    if (!/^\d{2}:\d{2}$/.test(timeString)) {
      return null;
    }
    const [hourStr, minuteStr] = timeString.split(':');
    let hours = Number.parseInt(hourStr, 10);
    let minutes = Number.parseInt(minuteStr, 10);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
      return null;
    }
    hours = Math.min(Math.max(hours, 0), 23);
    minutes = Math.min(Math.max(minutes, 0), 59);
    return [hours, minutes];
  }

  formatDuration(milliseconds) {
    const totalMinutes = Math.max(0, Math.round(milliseconds / 60000));
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;
    const parts = [];

    if (days) {
      parts.push(`${days}d`);
    }
    if (hours) {
      parts.push(`${hours}h`);
    }
    if (!days && minutes) {
      parts.push(`${minutes}m`);
    }
    if (parts.length === 0) {
      parts.push('<1m');
    }

    return parts.slice(0, 2).join(' ');
  }

  formatLeaveTargetLabel(slot) {
    if (!slot) {
      return '';
    }

    const { offsetDays, date, dayCode } = slot;
    const timeLabel = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

    if (offsetDays === 0) {
      return `today at ${timeLabel}`;
    }
    if (offsetDays === 1) {
      return `tomorrow at ${timeLabel}`;
    }

    const dayLabel = this.leaveDayMeta[dayCode]?.label ?? date.toLocaleDateString([], { weekday: 'long' });
    return `${dayLabel} at ${timeLabel}`;
  }

  getCalendarWriteConsent() {
    return (
      this.core.data.get('calendarWriteConsent', {
        enabled: false,
        grantedAt: null,
      }) || { enabled: false, grantedAt: null }
    );
  }

  setHeaderCollapsed(collapsed, options = {}) {
    // Delegate to UI module's setHeaderCollapsed method
    if (this.ui && typeof this.ui.setHeaderCollapsed === 'function') {
      return this.ui.setHeaderCollapsed(collapsed, options);
    }
    return false;
  }

  isCalendarWriteReady() {
    const consent = this.getCalendarWriteConsent();
    if (!consent?.enabled) {
      return false;
    }

    const integration = this.data.get('integrationState', {});
    const calendarState = integration.calendar || {};
    if (!calendarState.connected) {
      return false;
    }

    const hasScope =
      this.oauthClient?.hasScopes?.(GoogleCalendarService.WRITE_SCOPES) ??
      this.calendarService?.hasWriteAccess?.() ??
      false;

    return Boolean(hasScope);
  }

  composeCountdownStackEntries(now = new Date()) {
    const entries = [];
    const workEntry = this.buildWorkCountdownEntry(now);
    if (workEntry) {
      entries.push(workEntry);
    }

    const sleepEntry = this.buildSleepCountdownEntry(now);
    if (sleepEntry) {
      entries.push(sleepEntry);
    }

    const appointmentEntry = this.buildNextAppointmentEntry(now);
    if (appointmentEntry) {
      entries.push(appointmentEntry);
    }

    return entries;
  }

  buildWorkCountdownEntry(now = new Date()) {
    const countdown = this.getCountdownById(this.countdownIds?.work);
    const labelBase = countdown?.label || 'Leave for work';
    const scheduleConfigured =
      this.leaveSchedule?.configured && Array.isArray(this.leaveSchedule.entries) && this.leaveSchedule.entries.length > 0;

    if (!scheduleConfigured) {
      return {
        id: this.countdownIds?.work || 'system-work',
        label: labelBase,
        badge: 'WORK',
        status: 'calm',
        primaryText: 'Not set',
        secondaryText: 'Add your commute or routine times',
        primaryActionLabel: 'Set schedule',
        onPrimary: () => this.openLeaveScheduleModal(),
        sortWeight: 10,
        configured: false,
      };
    }

    const slots = this.computeLeaveScheduleSlots(now);
    const upcoming = slots.upcoming;

    if (!upcoming) {
      return {
        id: this.countdownIds?.work || 'system-work',
        label: labelBase,
        badge: 'WORK',
        status: 'calm',
        primaryText: 'All clear',
        secondaryText: 'No departures in the next 2 weeks',
        primaryActionLabel: 'Adjust',
        onPrimary: () => this.openLeaveScheduleModal(),
        sortWeight: 10,
        configured: true,
      };
    }

    const diffMs = upcoming.diffMs;
    const status = this.getCountdownStatus(diffMs);
    const primaryText = diffMs <= 30_000 ? 'Go now!' : this.formatDuration(diffMs);

    const secondaryPieces = [this.formatLeaveTargetLabel(upcoming)];
    if (upcoming.entry?.label) {
      secondaryPieces.push(upcoming.entry.label);
    }

    const entry = {
      id: this.countdownIds?.work || 'system-work',
      label: labelBase,
      badge: 'WORK',
      status,
      primaryText,
      secondaryText: secondaryPieces.filter(Boolean).join(' · '),
      primaryActionLabel: 'Adjust',
      onPrimary: () => this.openLeaveScheduleModal(),
      sortWeight: 10,
      configured: true,
      nextSlot: upcoming,
    };

    const canWrite = this.isCalendarWriteReady();
    if (canWrite && diffMs > 60_000) {
      entry.secondaryActionLabel = 'Block commute';
      entry.onSecondary = () => this.createLeaveCalendarBlock(upcoming);
    }

    return entry;
  }

  buildSleepCountdownEntry(now = new Date()) {
    const countdown = this.getCountdownById(this.countdownIds?.sleep);
    const settings = this.normalizeSleepSettings(countdown?.settings || this.sleepSettings);
    const label = countdown?.label || 'Wind down';

    const targetInfo = this.computeSleepTarget(now, settings);
    if (!targetInfo) {
      return {
        id: this.countdownIds?.sleep || 'system-sleep',
        label,
        badge: 'REST',
        status: 'calm',
        primaryText: 'Not set',
        secondaryText: 'Add your bedtime to start a wind-down countdown',
        primaryActionLabel: null,
        sortWeight: 20,
      };
    }

    const { windDownStart, bedtime, diffMs, windDownMinutes } = targetInfo;
    const status = this.getCountdownStatus(diffMs);
    const primaryText = diffMs <= 0 ? 'Time to wind down' : this.formatDuration(diffMs);

    const windDownLabel = windDownStart.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    const bedtimeLabel = bedtime.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

    const secondaryParts = [`Bedtime ${bedtimeLabel}`];
    if (windDownMinutes > 0) {
      secondaryParts.unshift(`Start at ${windDownLabel}`);
      secondaryParts.push(`lead ${windDownMinutes} min`);
    }

    return {
      id: this.countdownIds?.sleep || 'system-sleep',
      label,
      badge: 'REST',
      status,
      primaryText,
      secondaryText: secondaryParts.join(' · '),
      primaryActionLabel: null,
      sortWeight: 20,
    };
  }

  buildNextAppointmentEntry(now = new Date()) {
    const countdown = this.getCountdownById(this.countdownIds?.appointment);
    const label = countdown?.label || 'Next appointment';
    const events = this.data.get('calendar_events', []);

    let targetEvent = null;
    if (countdown?.calendarEventId) {
      targetEvent = events.find((event) => event.id === countdown.calendarEventId);
    }

    if (!targetEvent) {
      targetEvent = events
        .map((event) => ({
          ...event,
          startDate: event?.start ? new Date(event.start) : null,
        }))
        .filter((event) => event.startDate && !Number.isNaN(event.startDate.getTime()) && event.startDate.getTime() > now.getTime())
        .sort((a, b) => a.startDate.getTime() - b.startDate.getTime())[0];
    }

    if (!targetEvent) {
      return {
        id: this.countdownIds?.appointment || 'system-next-appointment',
        label,
        badge: 'APPT',
        status: 'calm',
        primaryText: 'No upcoming events',
        secondaryText: 'Connect Google Calendar to see appointments',
        primaryActionLabel: 'Refresh',
        onPrimary: () => this.refreshGoogleCalendarEvents({ silent: true }).catch(() => {}),
        sortWeight: 30,
      };
    }

    const startDate = targetEvent.startDate || new Date(targetEvent.start);
    if (!startDate || Number.isNaN(startDate.getTime())) {
      return null;
    }

    const offsetMinutes = Number(countdown?.offsetMinutes) || 0;
    const isLeaveMode = countdown?.mode === 'leaveBy';
    const targetDate = isLeaveMode
      ? new Date(startDate.getTime() - offsetMinutes * 60000)
      : startDate;

    const diffMs = targetDate.getTime() - now.getTime();
    const status = this.getCountdownStatus(diffMs);
    const primaryText = diffMs <= 0 ? 'Now' : this.formatDuration(diffMs);

    const startLabel = startDate.toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' });
    const secondaryText = isLeaveMode && offsetMinutes > 0
      ? `${startLabel} · leave ${offsetMinutes} min early`
      : startLabel;

    const entry = {
      id: this.countdownIds?.appointment || 'system-next-appointment',
      label: targetEvent.title || label,
      badge: 'APPT',
      status,
      primaryText,
      secondaryText,
      primaryActionLabel: 'Refresh',
      onPrimary: () => this.refreshGoogleCalendarEvents({ silent: true }).catch(() => {}),
      sortWeight: 30,
      sourceEvent: targetEvent,
      countdown,
    };

    if (this.isCalendarWriteReady() && diffMs > 60_000) {
      entry.secondaryActionLabel = 'Block focus';
      entry.onSecondary = () => this.createAppointmentPrepBlock(targetEvent, countdown);
    }

    return entry;
  }

  getCountdownStatus(diffMs) {
    if (diffMs <= 0) return 'critical';
    if (diffMs <= 15 * 60 * 1000) return 'warning';
    if (diffMs <= 2 * 60 * 60 * 1000) return 'calm';
    return 'calm';
  }

  computeSleepTarget(now = new Date(), settings = this.sleepSettings) {
    if (!settings || !settings.bedtime) {
      return null;
    }

    const timeParts = this.parseLeaveTime(settings.bedtime);
    if (!timeParts) {
      return null;
    }

    const [hours, minutes] = timeParts;
    const windDownMinutes = Number.isFinite(Number(settings.windDownMinutes))
      ? Math.max(0, Number(settings.windDownMinutes))
      : 0;

    for (let dayOffset = 0; dayOffset <= 1; dayOffset += 1) {
      const bedtime = new Date(now);
      bedtime.setHours(hours, minutes, 0, 0);
      bedtime.setDate(now.getDate() + dayOffset);

      const windDownStart = new Date(bedtime.getTime() - windDownMinutes * 60000);
      if (windDownStart.getTime() > now.getTime()) {
        return {
          windDownStart,
          bedtime,
          diffMs: windDownStart.getTime() - now.getTime(),
          windDownMinutes,
        };
      }
    }

    // Fallback to tomorrow night if today's window already passed
    const bedtime = new Date(now);
    bedtime.setDate(now.getDate() + 1);
    bedtime.setHours(timeParts[0], timeParts[1], 0, 0);
    const windDownStart = new Date(bedtime.getTime() - windDownMinutes * 60000);

    return {
      windDownStart,
      bedtime,
      diffMs: windDownStart.getTime() - now.getTime(),
      windDownMinutes,
    };
  }

  async createLeaveCalendarBlock(slot) {
    if (!slot || !this.isCalendarWriteReady()) {
      this.showNotification('Connect Google Calendar with write access to add commute blocks.');
      return;
    }

    if (!this.calendarService?.createEvent) {
      this.showNotification('Calendar write service is not ready yet. Try again shortly.');
      return;
    }

    const { entry, date } = slot;
    const bufferMinutes = Number(entry?.buffer) ?? 0;
    const end = new Date(date);
    const start = new Date(end.getTime() - Math.max(bufferMinutes || 15, 10) * 60_000);
    const now = new Date();

    if (end.getTime() <= now.getTime()) {
      this.showNotification('This leave time has already passed. Update your schedule and try again.');
      return;
    }

    if (start.getTime() <= now.getTime()) {
      start.setTime(now.getTime() + 60_000); // Start a minute from now if already in progress
    }

    const summary = entry?.label ? `Commute · ${entry.label}` : 'Commute / travel buffer';
    const description = 'Scheduled by Life Dashboard to protect travel time.';

    try {
      await this.calendarService.createEvent({
        summary,
        description,
        start,
        end,
      });
      this.showNotification('🚌 Commute block added to Google Calendar');
      this.track('calendar_write_commute_block_created', {
        bufferMinutes,
        leaveTime: end.toISOString(),
      });
      this.refreshGoogleCalendarEvents({ silent: true }).catch(() => {});
    } catch (error) {
      console.error('[LifeDashboard] Failed to create commute block', error);
      this.showNotification('⚠️ Could not add commute block. Try reconnecting Google Calendar.');
      this.track('calendar_write_commute_block_error', {
        message: error?.message || 'unknown',
      });
    }
  }

  async createAppointmentPrepBlock(event, countdown) {
    if (!event || !this.isCalendarWriteReady()) {
      this.showNotification('Enable calendar write access to create focus blocks.');
      return;
    }

    if (!this.calendarService?.createEvent) {
      this.showNotification('Calendar write service is not ready yet. Try again shortly.');
      return;
    }

    const startDate = event.start ? new Date(event.start) : null;
    if (!startDate || Number.isNaN(startDate.getTime())) {
      this.showNotification('Upcoming appointment is missing a valid start time.');
      return;
    }

    const now = new Date();
    const offsetMinutes = Number(countdown?.offsetMinutes) || 0;
    const prepMinutes = Math.max(offsetMinutes || 30, 15);

    const end = new Date(startDate);
    const start = new Date(end.getTime() - prepMinutes * 60_000);

    if (end.getTime() <= now.getTime()) {
      this.showNotification('That appointment has already started. Try another event.');
      return;
    }

    if (start.getTime() <= now.getTime()) {
      start.setTime(now.getTime() + 60_000);
    }

    const summary = `Focus prep: ${event.summary || 'Upcoming appointment'}`;
    const description = 'Blocked by Life Dashboard so you can prepare with calm focus.';

    try {
      await this.calendarService.createEvent({
        summary,
        description,
        start,
        end,
      });
      this.showNotification('🗓️ Focus block saved to Google Calendar');
      this.track('calendar_write_focus_block_created', {
        eventId: event.id,
        start: start.toISOString(),
        end: end.toISOString(),
      });
      this.refreshGoogleCalendarEvents({ silent: true }).catch(() => {});
    } catch (error) {
      console.error('[LifeDashboard] Failed to create appointment prep block', error);
      this.showNotification('⚠️ Unable to save focus block. Try reauthorizing calendar write access.');
      this.track('calendar_write_focus_block_error', {
        message: error?.message || 'unknown',
        eventId: event.id,
      });
    }
  }

  loadDebugConfiguration() {
    try {
      this.debug.headerToggle =
        window.localStorage?.getItem('lifeDashboardHeaderDebug') === '1';
    } catch (error) {
      console.warn('[LifeDashboard] Unable to read debug configuration', error);
      this.debug.headerToggle = false;
    }
  }

  setHeaderToggleDebug(enabled) {
    this.debug.headerToggle = Boolean(enabled);
    try {
      window.localStorage?.setItem('lifeDashboardHeaderDebug', enabled ? '1' : '0');
    } catch (error) {
      console.warn('[LifeDashboard] Unable to persist header toggle debug setting', error);
    }
    this.updateHeaderToggleState(this.isHeaderCollapsed(), { silent: true });
  }

  isHeaderCollapsed() {
    const welcomeSection = document.getElementById('welcome-section');
    return welcomeSection?.classList.contains('collapsed') ?? false;
  }

  updateHeaderToggleState(collapsed, options = {}) {
    const headerToggle = document.getElementById('header-toggle');
    if (!headerToggle) {
      return;
    }

    const icon = headerToggle.querySelector('.toggle-icon');
    if (icon) {
      icon.textContent = collapsed ? '▼' : '▲';
    }

    headerToggle.dataset.state = collapsed ? 'collapsed' : 'expanded';
    headerToggle.setAttribute('aria-expanded', (!collapsed).toString());
    headerToggle.setAttribute('aria-label', collapsed ? 'Expand header' : 'Collapse header');
    headerToggle.title = collapsed ? 'Expand header' : 'Collapse header';

    if (this.debug.headerToggle) {
      let computedTransform = 'n/a';
      if (icon) {
        computedTransform = window.getComputedStyle(icon).transform;
      }
      headerToggle.dataset.debug = `${collapsed ? 'collapsed' : 'expanded'} | ${computedTransform}`;
      headerToggle.classList.add('header-toggle--debug');

      if (!options.silent) {
        console.debug('[LifeDashboard] Header toggle state changed', {
          collapsed,
          iconText: icon?.textContent ?? null,
          computedTransform,
          timestamp: new Date().toISOString(),
        });
      }
    } else {
      headerToggle.classList.remove('header-toggle--debug');
      delete headerToggle.dataset.debug;
    }
  }

  logHeaderToggleState() {
    const collapsed = this.isHeaderCollapsed();
    this.updateHeaderToggleState(collapsed, { silent: false });
  }

  renderCompactMonth(today) {
    const container = document.getElementById('monthly-calendar-compact');
    if (!container) {
      return;
    }

    const year = today.getFullYear();
    const month = today.getMonth();
    const monthName = today.toLocaleDateString([], { month: 'long' }).toUpperCase();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startOffset = firstDay.getDay();
    const totalCells = Math.ceil((startOffset + daysInMonth) / 7) * 7;

    const dayCells = [];
    for (let cellIndex = 0; cellIndex < totalCells; cellIndex += 1) {
      const dayNumber = cellIndex - startOffset + 1;
      if (dayNumber < 1 || dayNumber > daysInMonth) {
        dayCells.push('<div class="compact-calendar__day is-empty"></div>');
      } else {
        const isToday = dayNumber === today.getDate();
        dayCells.push(`
          <div class="compact-calendar__day ${isToday ? 'is-today' : ''}" aria-label="${monthName} ${dayNumber}, ${year}">
            ${dayNumber}
          </div>
        `);
      }
    }

    container.innerHTML = `
      <div class="compact-calendar">
        <div class="compact-calendar__header">
          <span class="compact-calendar__month">${monthName} ${year}</span>
        </div>
        <div class="compact-calendar__grid">
          ${['S', 'M', 'T', 'W', 'T', 'F', 'S']
            .map(
              (day) => `
            <div class="compact-calendar__weekday">${day}</div>
          `
            )
            .join('')}
          ${dayCells.join('')}
        </div>
      </div>
    `;
  }

  renderCompactWeek(today) {
    const container = document.getElementById('weekly-calendar-compact');
    if (!container) {
      return;
    }

    const startOfWeek = new Date(today);
    startOfWeek.setHours(0, 0, 0, 0);
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());

    const weekDays = Array.from({ length: 7 }, (_, index) => {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + index);
      return day;
    });

    const rangeStart = weekDays[0].toLocaleDateString([], { month: 'short', day: 'numeric' });
    const rangeEnd = weekDays[6].toLocaleDateString([], { month: 'short', day: 'numeric' });

    // Get focus sessions for this week
    const focusSessions = this.getFocusSessionsForWeek(startOfWeek);

    const now = new Date();
    const currentTime = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const currentDate = now.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

    container.innerHTML = `
      <div class="compact-week">
        <div class="compact-week__header">
          <span class="compact-week__title">${currentTime}</span>
          <span class="compact-week__range">${currentDate}</span>
          <button class="btn btn--micro focus-planner-btn" title="Plan Focus Sessions">🔥 Plan</button>
        </div>
        <div class="compact-week__days">
          ${weekDays
            .map((day, index) => {
              const isToday = day.toDateString() === today.toDateString();
              const dayLabel = day.toLocaleDateString([], { weekday: 'short' }).toUpperCase();
              const dayNumber = day.getDate();
              const dayKey = day.toDateString();
              const dayFocusSessions = focusSessions.filter(
                (session) => new Date(session.date).toDateString() === dayKey
              );

              return `
              <div class="compact-week__day ${isToday ? 'is-today' : ''}" data-date="${dayKey}">
                <span class="compact-week__day-label">${dayLabel}</span>
                <span class="compact-week__date">${dayNumber}</span>
                <div class="focus-sessions">
                  ${
                    dayFocusSessions.length > 0
                      ? dayFocusSessions
                          .map(
                            (session) => `
                      <div class="focus-session-badge" title="${session.task}">
                        <span class="session-time">${session.time}</span>
                        <span class="session-icon">${session.type === 'pomodoro' ? '🍅' : '🔥'}</span>
                      </div>
                    `
                          )
                          .join('')
                      : `<button class="add-focus-btn" title="Add focus session">+</button>`
                  }
                </div>
              </div>
            `;
            })
            .join('')}
        </div>
      </div>

      <!-- Focus Session Planner Modal -->
      <div class="focus-planner-modal" style="display: none;">
        <div class="modal-backdrop"></div>
        <div class="modal-content">
          <div class="modal-header">
            <h3>🔥 Focus Session Planner</h3>
            <button class="modal-close">×</button>
          </div>
          <div class="modal-body">
            <div class="focus-form">
              <div class="form-group">
                <label>Task/Project</label>
                <select class="task-select">
                  <option value="">Select from projects...</option>
                </select>
                <input type="text" class="custom-task-input" placeholder="Or enter custom task...">
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label>Date</label>
                  <input type="date" class="focus-date-input">
                </div>
                <div class="form-group">
                  <label>Time</label>
                  <input type="time" class="focus-time-input">
                </div>
              </div>
              <div class="form-group">
                <label>Session Type</label>
                <div class="session-type-options">
                  <button type="button" class="session-type-btn active" data-type="pomodoro">
                    🍅 Pomodoro (25min)
                  </button>
                  <button type="button" class="session-type-btn" data-type="deep-work">
                    🔥 Deep Work (60min)
                  </button>
                  <button type="button" class="session-type-btn" data-type="custom">
                    ⏱️ Custom Duration
                  </button>
                </div>
                <input type="number" class="custom-duration" min="5" max="120" value="25" style="display: none;">
              </div>
              <div class="form-actions">
                <button class="btn btn--ghost cancel-focus">Cancel</button>
                <button class="btn save-focus-session">Schedule Session</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;

    // Attach event listeners for the new functionality
    this.attachFocusPlannerEvents(container);
  }

  getFocusSessionsForWeek(startOfWeek) {
    // Get focus sessions from data manager
    const sessions = this.data.get('focusSessions', []);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 7);

    return sessions.filter((session) => {
      const sessionDate = new Date(session.date);
      return sessionDate >= startOfWeek && sessionDate < endOfWeek;
    });
  }

  attachFocusPlannerEvents(container) {
    // Focus planner button
    const plannerBtn = container.querySelector('.focus-planner-btn');
    const modal = container.querySelector('.focus-planner-modal');
    const closeBtn = container.querySelector('.modal-close');
    const backdropBtn = container.querySelector('.modal-backdrop');
    const cancelBtn = container.querySelector('.cancel-focus');

    if (plannerBtn && modal) {
      plannerBtn.addEventListener('click', () => {
        this.openFocusPlanner(modal);
      });

      [closeBtn, backdropBtn, cancelBtn].forEach((btn) => {
        if (btn) {
          btn.addEventListener('click', () => {
            modal.style.display = 'none';
          });
        }
      });
    }

    // Add focus session buttons on individual days
    container.querySelectorAll('.add-focus-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const dayElement = e.target.closest('.compact-week__day');
        const selectedDate = dayElement.dataset.date;
        this.openFocusPlanner(modal, selectedDate);
      });
    });

    // Session type toggles
    container.querySelectorAll('.session-type-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        container
          .querySelectorAll('.session-type-btn')
          .forEach((b) => b.classList.remove('active'));
        e.target.classList.add('active');

        const customDuration = container.querySelector('.custom-duration');
        if (e.target.dataset.type === 'custom') {
          customDuration.style.display = 'block';
        } else {
          customDuration.style.display = 'none';
        }
      });
    });

    // Save focus session
    const saveBtn = container.querySelector('.save-focus-session');
    if (saveBtn) {
      saveBtn.addEventListener('click', () => {
        this.saveFocusSession(container);
      });
    }
  }

  openFocusPlanner(modal, selectedDate = null) {
    const dateInput = modal.querySelector('.focus-date-input');
    const taskSelect = modal.querySelector('.task-select');

    // Set default date if provided
    if (selectedDate) {
      const date = new Date(selectedDate);
      dateInput.value = date.toISOString().split('T')[0];
    }

    // Populate task select with current projects
    this.populateTaskSelect(taskSelect);

    modal.style.display = 'flex';

    // Focus on first input
    const customTaskInput = modal.querySelector('.custom-task-input');
    if (customTaskInput) {
      customTaskInput.focus();
    }
  }

  populateTaskSelect(selectElement) {
    // Get projects from projects table (if available)
    const projects = this.data.get('projects', []);

    selectElement.innerHTML = '<option value="">Select from projects...</option>';

    projects.forEach((project) => {
      const option = document.createElement('option');
      option.value = project.name;
      option.textContent = project.name;
      selectElement.appendChild(option);
    });

    // Add some default focus session types
    const defaultSessions = [
      'Deep Work Session',
      'Creative Thinking',
      'Admin & Organization',
      'Learning & Research',
      'Writing & Documentation',
    ];

    defaultSessions.forEach((session) => {
      const option = document.createElement('option');
      option.value = session;
      option.textContent = session;
      selectElement.appendChild(option);
    });
  }

  saveFocusSession(container) {
    const modal = container.querySelector('.focus-planner-modal');
    const taskSelect = modal.querySelector('.task-select');
    const customTaskInput = modal.querySelector('.custom-task-input');
    const dateInput = modal.querySelector('.focus-date-input');
    const timeInput = modal.querySelector('.focus-time-input');
    const activeType = modal.querySelector('.session-type-btn.active');
    const customDuration = modal.querySelector('.custom-duration');

    // Get form values
    const task = customTaskInput.value.trim() || taskSelect.value;
    const date = dateInput.value;
    const time = timeInput.value;
    const type = activeType ? activeType.dataset.type : 'pomodoro';

    // Validation
    if (!task || !date || !time) {
      alert('Please fill in all required fields');
      return;
    }

    // Create session object
    const session = {
      id: Date.now().toString(),
      task,
      date,
      time,
      type,
      duration:
        type === 'custom' ? parseInt(customDuration.value, 10) : type === 'deep-work' ? 60 : 25,
      created: new Date().toISOString(),
      completed: false,
    };

    // Save to data manager
    const sessions = this.data.get('focusSessions', []);
    sessions.push(session);
    this.data.set('focusSessions', sessions);

    // Close modal and refresh display
    modal.style.display = 'none';
    this.renderCompactCalendars();

    // Show confirmation
    this.showNotification(`🔥 Focus session scheduled for ${task}!`);
  }

  showNotification(message, duration = 3000) {
    const notification = document.createElement('div');
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: var(--color-primary);
      color: white;
      padding: var(--spacing-sm) var(--spacing-lg);
      border-radius: var(--radius-md);
      font-size: var(--text-sm);
      font-weight: var(--font-medium);
      z-index: 1000;
      animation: slideInRight 0.3s ease-out;
      box-shadow: var(--shadow-medium);
      max-width: 300px;
    `;

    document.body.appendChild(notification);
    setTimeout(() => notification.remove(), duration);
  }

  initializeSettingsMenu() {
    const settingsMenu = document.getElementById('settings-menu');
    const settingsToggle = document.getElementById('settings-toggle');
    const settingsDropdown = document.getElementById('settings-dropdown');
    const settingsClose = document.getElementById('settings-close');
    const logoutButton = document.getElementById('logout-button');

    // Feature toggle buttons
    const focusModeToggle = document.getElementById('focus-mode-toggle');
    const stimmingCornerToggle = document.getElementById('stimming-corner-toggle');
    const floatingNotesToggle = document.getElementById('floating-notes-toggle');
    const celebrationsToggle = document.getElementById('celebrations-toggle');
    const autoThemeToggle = document.getElementById('auto-theme-toggle');

    const googleCalendarButton = document.getElementById('google-calendar-connect');
    const googleCalendarWriteButton = document.getElementById('google-calendar-write');

    if (settingsToggle && settingsDropdown && settingsMenu) {
      settingsToggle.addEventListener('click', (event) => {
        event.stopPropagation();
        this.toggleSettingsMenu();
      });

      if (settingsClose) {
        settingsClose.addEventListener('click', (event) => {
          event.preventDefault();
          this.closeSettingsMenu();
        });
      }

      document.addEventListener('click', (event) => {
        if (!settingsMenu.contains(event.target)) {
          this.closeSettingsMenu();
        }
      });

      // Handle logout button
      if (logoutButton) {
        logoutButton.addEventListener('click', () => {
          this.handleLogout();
        });
      }

      // Handle feature toggles
      if (focusModeToggle) {
        focusModeToggle.addEventListener('click', () => {
          const isEnabled = this.featureFlags.isEnabled('focusMode');
          this.featureFlags.set('focusMode', !isEnabled);

          // Show notification that page reload is needed
          const message = !isEnabled
            ? '🎯 Focus Mode enabled! Reload the page to activate.'
            : '🎯 Focus Mode disabled. Reload to return to traditional layout.';

          this.showNotification(message);
          this.updateSettingsStates();
        });
      }

      if (stimmingCornerToggle) {
        stimmingCornerToggle.addEventListener('click', () => {
          this.toggleStimmingCorner();
          this.updateSettingsStates();
        });
      }

      if (floatingNotesToggle) {
        floatingNotesToggle.addEventListener('click', () => {
          this.toggleFloatingNotes();
          this.updateSettingsStates();
        });
      }

      if (celebrationsToggle) {
        celebrationsToggle.addEventListener('click', () => {
          this.toggleCelebrations();
          this.updateSettingsStates();
        });
      }

      if (autoThemeToggle) {
        autoThemeToggle.addEventListener('click', () => {
          const isEnabled = this.data.get('autoThemeEnabled', true);
          this.setAutoThemeEnabled(!isEnabled);
          this.updateSettingsStates();
        });
      }

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.closeSettingsMenu();
        }
      });
    }

    if (googleCalendarButton) {
      googleCalendarButton.addEventListener('click', () => {
        this.handleGoogleCalendarAction();
      });
    }

    if (googleCalendarWriteButton) {
      googleCalendarWriteButton.addEventListener('click', () => {
        this.handleGoogleCalendarWriteAction();
      });
    }

    if (typeof this.core.data.subscribe === 'function') {
      this.core.data.subscribe('integrationState', () => {
        this.updateSettingsStates();
      });
      this.core.data.subscribe('calendarWriteConsent', () => {
        this.updateSettingsStates();
      });
    }

    this.updateSettingsStates();
  }

  toggleSettingsMenu() {
    const settingsDropdown = document.getElementById('settings-dropdown');
    if (!settingsDropdown) {
      return;
    }

    if (settingsDropdown.classList.contains('active')) {
      this.closeSettingsMenu();
    } else {
      this.openSettingsMenu();
    }
  }

  openSettingsMenu() {
    const settingsToggle = document.getElementById('settings-toggle');
    const settingsDropdown = document.getElementById('settings-dropdown');
    if (settingsToggle && settingsDropdown) {
      settingsDropdown.classList.add('active');
      settingsToggle.classList.add('is-active');
      settingsToggle.setAttribute('aria-expanded', 'true');
      this.updateSettingsStates();
    }
  }

  closeSettingsMenu() {
    const settingsToggle = document.getElementById('settings-toggle');
    const settingsDropdown = document.getElementById('settings-dropdown');
    if (settingsToggle && settingsDropdown) {
      settingsDropdown.classList.remove('active');
      settingsToggle.classList.remove('is-active');
      settingsToggle.setAttribute('aria-expanded', 'false');
    }
  }

  // setControlState method removed - no longer needed

  // updateThemePills method removed - no longer needed

  updateSettingsStates() {
    // Update toggle button states to show active/inactive
    const focusModeToggle = document.getElementById('focus-mode-toggle');
    const focusModeStatus = document.getElementById('focus-mode-status');
    const stimmingCornerToggle = document.getElementById('stimming-corner-toggle');
    const floatingNotesToggle = document.getElementById('floating-notes-toggle');
    const celebrationsToggle = document.getElementById('celebrations-toggle');
    const autoThemeToggle = document.getElementById('auto-theme-toggle');
    const calendarButton = document.getElementById('google-calendar-connect');
    const calendarDescription = document.getElementById('google-calendar-description');
    const calendarStatus = document.getElementById('google-calendar-status');
    const calendarWriteButton = document.getElementById('google-calendar-write');
    const calendarWriteDescription = document.getElementById('google-calendar-write-description');
    const calendarWriteStatus = document.getElementById('google-calendar-write-status');

    // Focus Mode toggle
    if (focusModeToggle && focusModeStatus) {
      const isEnabled = this.core.featureFlags.isEnabled('focusMode');
      focusModeToggle.classList.toggle('is-active', isEnabled);
      focusModeStatus.textContent = isEnabled ? 'On' : 'Off';
      focusModeStatus.classList.toggle('status-on', isEnabled);
    }

    // Stimming Corner toggle
    if (stimmingCornerToggle) {
      const isEnabled = this.core.data.get('stimmingCornerEnabled', false);
      stimmingCornerToggle.classList.toggle('is-active', isEnabled);
    }

    // Floating Notes toggle
    if (floatingNotesToggle) {
      const isEnabled = this.core.data.get('floatingNotesEnabled', false);
      floatingNotesToggle.classList.toggle('is-active', isEnabled);
    }

    // Celebrations toggle
    if (celebrationsToggle) {
      const isEnabled = this.core.data.get('celebrationsEnabled', true);
      celebrationsToggle.classList.toggle('is-active', isEnabled);
    }

    // Auto Theme toggle
    if (autoThemeToggle) {
      const isEnabled = this.core.data.get('autoThemeEnabled', true);
      autoThemeToggle.classList.toggle('is-active', isEnabled);
    }

    if (calendarButton && calendarDescription && calendarStatus) {
      const integrationState = this.core.data.get('integrationState', {});
      const calendarState = integrationState.calendar || {};
      const flagEnabled = this.core.featureFlags?.isEnabled?.('googleCalendarRead') ?? false;
      const status = calendarState.status || (flagEnabled ? 'ready' : 'disabled');
      const statusMeta = {
        disabled: {
          label: 'Disabled',
          className: '',
          description: 'Enable this integration to see upcoming events inside the dashboard.',
        },
        pending_configuration: {
          label: 'Configure',
          className: 'is-warning',
          description: 'Add a Google OAuth client ID to connect your calendar.',
        },
        initializing: {
          label: 'Checking…',
          className: 'is-warning',
          description: 'Preparing Google Calendar integration…',
        },
        ready: {
          label: 'Ready',
          className: 'is-ready',
          description: 'Connect Google Calendar to pull in your upcoming schedule.',
        },
        disconnected: {
          label: 'Ready',
          className: 'is-ready',
          description: 'Connect Google Calendar to pull in your upcoming schedule.',
        },
        authorizing: {
          label: 'Authorizing…',
          className: 'is-warning',
          description: 'Complete the Google consent screen to finish connecting.',
        },
        exchanging: {
          label: 'Connecting…',
          className: 'is-warning',
          description: 'Finishing the calendar connection…',
        },
        connected: {
          label: 'Connected',
          className: 'is-connected',
          description: 'Google Calendar events are syncing into the Weekly Calendar.',
        },
        error: {
          label: 'Error',
          className: 'is-error',
          description: 'We could not connect. Click to try again.',
        },
      };

      const meta = statusMeta[status] || statusMeta.disabled;
      const busyStatuses = new Set(['initializing', 'authorizing', 'exchanging']);

      calendarButton.disabled = busyStatuses.has(status);
      calendarButton.dataset.state = status;
      calendarButton.classList.toggle('is-active', Boolean(calendarState.connected));
      calendarButton.setAttribute('aria-busy', busyStatuses.has(status) ? 'true' : 'false');

      calendarStatus.className = 'settings-item__status';
      if (meta.className) {
        calendarStatus.classList.add(meta.className);
      }

      calendarStatus.textContent = meta.label;

      let description = meta.description;

      if (!flagEnabled) {
        description = 'Click to enable Google Calendar support for this dashboard.';
      } else if (status === 'connected') {
        const lastSync = this.formatRelativeTime(calendarState.lastSync);
        if (lastSync) {
          description = `Synced ${lastSync}. Click to disconnect if needed.`;
        } else {
          description = 'Connected and syncing events. Click to disconnect.';
        }
      } else if (status === 'error' && calendarState.error) {
        description = `${meta.description} (${calendarState.error})`;
      } else if (status === 'pending_configuration') {
        calendarButton.disabled = false;
      }

      calendarDescription.textContent = description;
      calendarButton.title = status === 'error' ? calendarState.error || meta.description : description;
    }

    if (calendarWriteButton && calendarWriteDescription && calendarWriteStatus) {
      const integrationState = this.core.data.get('integrationState', {});
      const calendarState = integrationState.calendar || {};
      const consent = this.getCalendarWriteConsent();
      const hasScope = this.oauthClient?.hasScopes?.(GoogleCalendarService.WRITE_SCOPES) ?? false;
      const busy = this.isCalendarBusyStatus(calendarState.status);
      const connected = Boolean(calendarState.connected);

      calendarWriteButton.disabled = !connected || busy;
      calendarWriteButton.setAttribute('aria-busy', busy ? 'true' : 'false');
      calendarWriteButton.classList.toggle('is-active', connected && consent.enabled && hasScope);

      let label = 'Off';
      let description = 'Optional: allow the dashboard to add focus blocks to your Google Calendar.';
      let statusClass = '';

      if (!connected) {
        label = 'Connect calendar';
        description = 'First connect Google Calendar to unlock optional write access.';
        statusClass = 'is-warning';
      } else if (busy) {
        label = 'Working…';
        description = 'Finishing calendar sync before changing write access.';
        statusClass = 'is-warning';
      } else if (consent.enabled && hasScope) {
        label = 'Enabled';
        description = 'We can add commute or focus blocks when you choose.';
        statusClass = 'is-connected';
      } else if (!consent.enabled && hasScope) {
        label = 'Paused';
        description = 'Write scope granted, but we will stay read-only until you enable it here.';
        statusClass = 'is-warning';
      }

      calendarWriteStatus.className = 'settings-item__status';
      if (statusClass) {
        calendarWriteStatus.classList.add(statusClass);
      }
      calendarWriteStatus.textContent = label;
      calendarWriteDescription.textContent = description;
      calendarWriteButton.title = description;
    }
  }

  formatRelativeTime(timestamp) {
    if (!timestamp) return null;
    try {
      const date = new Date(timestamp);
      if (Number.isNaN(date.getTime())) {
        return null;
      }

      const diff = Date.now() - date.getTime();
      const minute = 60 * 1000;
      const hour = 60 * minute;
      const day = 24 * hour;

      if (diff < 0) {
        return 'just now';
      }
      if (diff < minute) {
        return 'just now';
      }
      if (diff < hour) {
        const minutes = Math.round(diff / minute);
        return `${minutes} min ago`;
      }
      if (diff < day) {
        const hours = Math.round(diff / hour);
        return `${hours} hr${hours > 1 ? 's' : ''} ago`;
      }
      if (diff < day * 7) {
        const days = Math.round(diff / day);
        return `${days} day${days > 1 ? 's' : ''} ago`;
      }

      return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return null;
    }
  }

  setAutoThemeEnabled(enabled, options = {}) {
    const { silent = false } = options;
    const currentlyAuto = !this.themeController?.manualOverride;

    if (enabled === currentlyAuto) {
      this.updateSettingsStates();
      return;
    }

    if (enabled) {
      this.themeController?.enableAutoMode();
      if (!silent) {
        this.showNotification('🪄 Auto theme enabled');
      }
    } else {
      this.themeController.manualOverride = true;
      localStorage.setItem('dashboard-theme-manual', 'true');
      localStorage.setItem('dashboard-theme', this.themeController.currentTheme);
      if (!silent) {
        this.showNotification('🎚️ Manual theme control enabled');
      }
    }

    this.updateSettingsStates();
  }

  applyDayMode(isDayMode) {
    const dayModeToggle = document.getElementById('day-mode-toggle');

    if (isDayMode) {
      document.body.setAttribute('data-day-mode', 'true');
    } else {
      document.body.removeAttribute('data-day-mode');
    }

    if (dayModeToggle) {
      dayModeToggle.classList.toggle('active', isDayMode);
    }

    this.updateSettingsStates();
  }

  toggleEmergencyMode() {
    const emergencyToggle = document.getElementById('emergency-toggle');
    const isActive = this.data.get('emergencyMode', false);
    const newState = !isActive;

    this.data.set('emergencyMode', newState);

    document.body.classList.toggle('emergency-mode', newState);

    if (newState) {
      document.body.setAttribute('data-emergency-mode', 'true');
    } else {
      document.body.removeAttribute('data-emergency-mode');
    }

    if (emergencyToggle) {
      emergencyToggle.classList.toggle('active', newState);

      const icon = emergencyToggle.querySelector('.item-icon');
      if (icon) {
        icon.textContent = newState ? '✅' : '🚨';
      }

      const label = emergencyToggle.querySelector('.item-text');
      if (label) {
        label.textContent = newState ? 'Exit Emergency' : 'Emergency Mode';
      } else {
        emergencyToggle.textContent = newState ? '✅ Exit Emergency' : '🚨 Emergency';
      }
    }

    if (newState) {
      this.showEmergencyInterface();
      this.showEmergencyGuidance();
    }

    this.updateSettingsStates();
  }

  toggleFocusMode() {
    const focusToggle = document.getElementById('focus-toggle');
    const body = document.body;
    const isFocusMode = body.hasAttribute('data-focus-mode');

    if (isFocusMode) {
      body.removeAttribute('data-focus-mode');
      focusToggle?.classList.remove('active');
    } else {
      body.setAttribute('data-focus-mode', 'true');
      focusToggle?.classList.add('active');
      // Hide non-essential elements for better focus
      this.showFocusInterface();
    }

    this.updateSettingsStates();
  }

  showEmergencyInterface() {
    // Emergency mode shows only essential elements
    console.log('Emergency mode activated - showing crisis support interface');
  }

  showFocusInterface() {
    // Focus mode hides distractions
    console.log('Focus mode activated - minimizing distractions');
  }

  renderADHDPlaceholders() {
    // Note: Focus Flow is now integrated into the weekly calendar

    // Other placeholder content would go here if needed
    const placeholderContainer = document.getElementById('placeholder-container');
    if (placeholderContainer) {
      placeholderContainer.innerHTML = `
        <div class="placeholder-content">
          <p class="placeholder-text">Dashboard components loaded</p>
          <div class="placeholder-features">
            <span class="feature-tag">Pomodoro Timer</span>
            <span class="feature-tag">Hyperfocus Mode</span>
            <span class="feature-tag">Task Priority</span>
            <span class="feature-tag">Context Switching</span>
          </div>
        </div>
      `;
    }

    // Energy & Mood placeholder
    const energyMoodContainer = document.getElementById('energy-mood-container');
    if (energyMoodContainer) {
      energyMoodContainer.innerHTML = `
        <div class="placeholder-content">
          <p class="placeholder-text">⚡ ADHD Energy & Mood Tracking</p>
          <div class="placeholder-features">
            <span class="feature-tag">Energy Levels</span>
            <span class="feature-tag">Mood Check-ins</span>
            <span class="feature-tag">Pattern Recognition</span>
            <span class="feature-tag">Stimming Support</span>
          </div>
        </div>
      `;
    }

    // Initialize ADHD support widgets
    this.initializeADHDWidgets();
  }

  attachGlobalEvents() {
    window.addEventListener('message', this.handleOAuthWindowMessage);

    // Add project button handler - use event delegation to avoid multiple listeners
    document.addEventListener('click', (event) => {
      if (event.target.classList.contains('add-project-btn')) {
        event.preventDefault();
        this.showNewProjectModal();
      }
    });

    // Emergency mode toggle for any global trigger outside settings menu
    document.querySelectorAll('.emergency-toggle').forEach((button) => {
      if (button.closest('#settings-menu')) {
        return;
      }
      button.addEventListener('click', () => this.toggleEmergencyMode());
    });

    // Global keyboard shortcuts
    document.addEventListener('keydown', (e) => {
      // Ctrl/Cmd + E for emergency mode
      if ((e.ctrlKey || e.metaKey) && e.key === 'e') {
        e.preventDefault();
        this.toggleEmergencyMode();
      }

      // Escape to cancel sprint
      if (e.key === 'Escape' && this.data.get('sprintActive', false)) {
        this.modules.topThree?.stopSprint?.();
      }
    });

    // Handle visibility changes (page focus/blur)
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // Page is hidden - could pause timers if needed
        console.log('Dashboard hidden');
      } else {
        // Page is visible - refresh data if needed
        console.log('Dashboard visible');
        this.refreshData();
      }
    });
  }

  initializeSidebarToggle() {
    const sidebar = document.getElementById('guided-flow-sidebar');
    const toggleBtn = document.getElementById('sidebar-toggle');
    const closeBtn = document.getElementById('guided-flow-close');

    console.log('[Sidebar] Initializing sidebar toggle', { sidebar, toggleBtn, closeBtn });

    if (!sidebar || !toggleBtn) {
      console.warn('[Sidebar] Elements not found', {
        sidebar: !!sidebar,
        toggleBtn: !!toggleBtn
      });
      return;
    }

    console.log('[Sidebar] Elements found, setting up listeners');

    // Load saved state
    const savedState = localStorage.getItem('guidedFlowSidebarOpen');
    if (savedState === 'true') {
      sidebar.classList.add('open');
      console.log('[Sidebar] Restored open state from localStorage');
    }

    // Toggle sidebar
    const toggleSidebar = () => {
      const isOpen = sidebar.classList.toggle('open');
      localStorage.setItem('guidedFlowSidebarOpen', isOpen);
      console.log('[Sidebar] Toggled:', isOpen);
    };

    // Close sidebar
    const closeSidebar = () => {
      sidebar.classList.remove('open');
      localStorage.setItem('guidedFlowSidebarOpen', 'false');
      console.log('[Sidebar] Closed');
    };

    toggleBtn.addEventListener('click', (e) => {
      console.log('[Sidebar] Toggle button clicked', e);
      toggleSidebar();
    });

    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        console.log('[Sidebar] Close button clicked', e);
        closeSidebar();
      });
    }

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && sidebar.classList.contains('open')) {
        console.log('[Sidebar] Escape key pressed');
        closeSidebar();
      }
    });

    console.log('[Sidebar] Initialization complete');

    // Add global debug helper
    window.debugSidebar = {
      toggle: toggleSidebar,
      open: () => {
        sidebar.classList.add('open');
        console.log('[Sidebar] Manually opened');
      },
      close: closeSidebar,
      isOpen: () => sidebar.classList.contains('open')
    };
  }

  initializeADHDWidgets() {
    this.stimmingEnabled = this.core.data.get('stimmingEnabled', false);
    this.celebrationsEnabled = this.core.data.get('celebrationsEnabled', true);
    this.floatingNotesEnabled = this.core.data.get('floatingNotesEnabled', true);

    // Stimming corner button functionality
    const stimmingBtn = document.querySelector('.stimming-btn');
    if (stimmingBtn) {
      stimmingBtn.addEventListener('click', () => this.toggleStimmingCorner());
    }

    // Apply stored preferences
    this.setStimmingCornerEnabled(this.stimmingEnabled, { animate: false, persist: false });

    // Initialize fidget widgets
    this.initializeFidgetWidgets();

    // Add floating working memory support
    this.initializeFloatingNotes();

    // Add celebration system
    this.celebrationCount = 0;

    this.updateSettingsStates();
  }

  setStimmingCornerEnabled(enabled, options = {}) {
    const { animate = true, persist = true } = options;
    const stimmingCorner = document.getElementById('stimming-corner');

    this.stimmingEnabled = enabled;

    if (persist) {
      this.core.data.set('stimmingEnabled', enabled);
    }

    if (!stimmingCorner) {
      this.updateSettingsStates();
      return;
    }

    if (enabled) {
      stimmingCorner.style.display = 'block';
      if (animate) {
        stimmingCorner.style.animation = 'slideInRight 0.3s ease-out';
      } else {
        stimmingCorner.style.animation = '';
      }
    } else if (animate) {
      stimmingCorner.style.animation = 'slideOutRight 0.3s ease-out';
      setTimeout(() => {
        if (!this.stimmingEnabled) {
          stimmingCorner.style.display = 'none';
        }
      }, 300);
    } else {
      stimmingCorner.style.display = 'none';
      stimmingCorner.style.animation = '';
    }

    this.updateSettingsStates();
  }

  toggleStimmingCorner() {
    this.setStimmingCornerEnabled(!this.stimmingEnabled);
  }

  initializeFidgetWidgets() {
    // Stress ball interaction
    const stressBall = document.getElementById('stress-ball');
    if (stressBall) {
      stressBall.addEventListener('click', () => {
        stressBall.style.transform = 'scale(0.8)';
        setTimeout(() => {
          stressBall.style.transform = 'scale(1)';
        }, 100);
        this.playHapticFeedback();
      });
    }

    // Spinner interaction
    const spinner = document.getElementById('spinner');
    if (spinner) {
      spinner.addEventListener('click', () => {
        spinner.style.animationDuration = '0.2s';
        setTimeout(() => {
          spinner.style.animationDuration = '3s';
        }, 2000);
      });
    }

    // Breathing circle
    const breathingCircle = document.getElementById('breathing-circle');
    if (breathingCircle) {
      breathingCircle.addEventListener('click', () => {
        breathingCircle.style.animationDuration = '2s';
        this.showFloatingNote('Focus on your breathing 🫁', 4000);
        setTimeout(() => {
          breathingCircle.style.animationDuration = '4s';
        }, 10000);
      });
    }
  }

  initializeFloatingNotes() {
    // Add working memory support notes
    this.workingMemoryNotes = [];

    if (this.floatingNotesEnabled) {
      this.startFloatingNotesInterval();
    } else {
      this.stopFloatingNotesInterval();
      const floatingNotes = document.getElementById('floating-notes');
      if (floatingNotes) {
        floatingNotes.innerHTML = '';
      }
    }
  }

  startFloatingNotesInterval() {
    this.stopFloatingNotesInterval();

    this.floatingNotesInterval = setInterval(
      () => {
        if (document.visibilityState === 'visible' && this.floatingNotesEnabled) {
          this.showFloatingNote('💭 Quick brain check - what were you doing?', 5000);
        }
      },
      25 * 60 * 1000
    );
  }

  stopFloatingNotesInterval() {
    if (this.floatingNotesInterval) {
      clearInterval(this.floatingNotesInterval);
      this.floatingNotesInterval = null;
    }
  }

  setFloatingNotesEnabled(enabled, options = {}) {
    const { persist = true, silent = false } = options;

    this.floatingNotesEnabled = enabled;

    if (enabled) {
      this.startFloatingNotesInterval();
      if (!silent) {
        this.showNotification('💭 Working memory reminders on');
      }
    } else {
      this.stopFloatingNotesInterval();
      const floatingNotes = document.getElementById('floating-notes');
      if (floatingNotes) {
        floatingNotes.innerHTML = '';
      }
      if (!silent) {
        this.showNotification('💤 Working memory reminders paused');
      }
    }

    if (persist) {
      this.data.set('floatingNotesEnabled', enabled);
    }

    this.updateSettingsStates();
  }

  toggleFloatingNotes() {
    this.setFloatingNotesEnabled(!this.floatingNotesEnabled);
  }

  showFloatingNote(message, duration = 3000) {
    const floatingNotes = document.getElementById('floating-notes');
    const note = document.createElement('div');
    note.className = 'floating-note';
    note.textContent = message;

    note.addEventListener('click', () => note.remove());

    floatingNotes.appendChild(note);

    setTimeout(() => {
      if (note.parentElement) {
        note.remove();
      }
    }, duration);
  }

  setCelebrationsEnabled(enabled, options = {}) {
    const { persist = true, silent = false } = options;

    this.celebrationsEnabled = enabled;

    if (persist) {
      this.data.set('celebrationsEnabled', enabled);
    }

    if (!silent) {
      this.showNotification(enabled ? '🎉 Mini celebrations on' : '🔕 Celebrations muted');
    }

    this.updateSettingsStates();
  }

  toggleCelebrations() {
    this.setCelebrationsEnabled(!this.celebrationsEnabled);
  }

  triggerCelebration(message = '🎉') {
    if (!this.celebrationsEnabled) {
      return;
    }

    this.celebrationCount++;

    // Create confetti
    const celebrationOverlay = document.getElementById('celebration-overlay');
    for (let i = 0; i < 20; i++) {
      const confetti = document.createElement('div');
      confetti.className = 'confetti';
      confetti.style.left = Math.random() * 100 + 'vw';
      confetti.style.backgroundColor = this.getRandomColor();
      confetti.style.animationDelay = Math.random() * 2 + 's';

      celebrationOverlay.appendChild(confetti);

      setTimeout(() => confetti.remove(), 3000);
    }

    // Show celebration message
    this.showFloatingNote(`${message} Celebration #${this.celebrationCount}!`, 2000);

    // Play sound if available
    this.playHapticFeedback();
  }

  getRandomColor() {
    const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#f9ca24', '#f0932b', '#eb4d4b', '#6c5ce7'];
    return colors[Math.floor(Math.random() * colors.length)];
  }

  playHapticFeedback() {
    // Try to use haptic feedback on mobile devices
    if ('vibrate' in navigator) {
      navigator.vibrate(50);
    }
  }

  showEmergencyGuidance() {
    // Simple guidance overlay for emergency mode
    const guidance = document.createElement('div');
    guidance.className = 'emergency-guidance';
    guidance.innerHTML = `
      <div class="guidance-content">
        <h3>🚨 Emergency Mode Active</h3>
        <p>Focus on just ONE thing:</p>
        <ul>
          <li>Choose your most urgent task</li>
          <li>Start a sprint timer</li>
          <li>Ignore everything else</li>
        </ul>
        <button onclick="this.parentElement.parentElement.remove()">Got it</button>
      </div>
    `;

    guidance.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
    `;

    document.body.appendChild(guidance);

    // Auto-hide after 5 seconds
    setTimeout(() => {
      if (guidance.parentElement) {
        guidance.remove();
      }
    }, 5000);
  }

  refreshData() {
    this.refreshGoogleCalendarEvents({ silent: true });

    // Refresh all modules - useful when returning to the page
    Object.values(this.modules).forEach((module) => {
      if (module.render) {
        module.render();
      }
    });
  }

  showNewProjectModal() {
    // Check if modal already exists
    const existingModal = document.querySelector('.project-modal');
    if (existingModal) {
      existingModal.remove();
    }

    const modal = document.createElement('div');
    modal.className = 'project-modal';
    modal.innerHTML = `
      <div class="project-modal__backdrop"></div>
      <div class="project-modal__dialog">
        <h3>Create New Project</h3>
        <form class="project-form">
          <div class="form-group">
            <label for="project-name">Project Name</label>
            <input type="text" id="project-name" placeholder="Enter project name" required>
          </div>

          <div class="form-group">
            <label for="project-category">Category</label>
            <select id="project-category">
              <option value="Teaching">📚 Teaching</option>
              <option value="Coaching">🎯 Coaching</option>
              <option value="ADHD Projects">🧠 ADHD Projects</option>
              <option value="Economics">💰 Economics</option>
            </select>
          </div>

          <div class="form-group">
            <label for="project-priority">Priority</label>
            <select id="project-priority">
              <option value="high">🔥 High</option>
              <option value="medium" selected>⚡ Medium</option>
              <option value="low">🌱 Low</option>
            </select>
          </div>

          <div class="form-group">
            <label for="project-todos">Initial Todos</label>
            <textarea id="project-todos" placeholder="What needs to be done first?"></textarea>
          </div>

          <div class="form-group">
            <label for="project-repo">GitHub Repo URL (optional)</label>
            <input type="url" id="project-repo" placeholder="https://github.com/username/repo">
          </div>

          <div class="form-group">
            <label for="project-obsidian">Obsidian Note Name (optional)</label>
            <input type="text" id="project-obsidian" placeholder="Project Notes">
          </div>

          <div class="form-actions">
            <button type="button" class="btn btn--secondary cancel-btn">Cancel</button>
            <button type="submit" class="btn btn--primary">Create Project</button>
          </div>
        </form>
      </div>
    `;

    document.body.appendChild(modal);

    // Event handlers
    const form = modal.querySelector('.project-form');
    const cancelBtn = modal.querySelector('.cancel-btn');
    const backdrop = modal.querySelector('.project-modal__backdrop');

    const closeModal = () => {
      modal.remove();
    };

    cancelBtn.addEventListener('click', closeModal);
    backdrop.addEventListener('click', closeModal);

    // Also add click handler for the submit button as backup
    const submitBtn = modal.querySelector('button[type="submit"]');
    if (submitBtn) {
      submitBtn.addEventListener('click', (e) => {
        e.preventDefault();
        form.dispatchEvent(new Event('submit'));
      });
    }

    form.addEventListener('submit', (e) => {
      e.preventDefault();

      const projectName = document.getElementById('project-name').value.trim();

      if (!projectName) {
        alert('Please enter a project name');
        document.getElementById('project-name').focus();
        return;
      }

      const projectData = {
        name: projectName,
        category: document.getElementById('project-category').value,
        priority: document.getElementById('project-priority').value,
        todos: document.getElementById('project-todos').value.trim(),
        repoUrl: document.getElementById('project-repo').value.trim(),
        obsidianNote: document.getElementById('project-obsidian').value.trim(),
      };
      this.modules.projectsTable.addProject(projectData);
      closeModal();
    });

    // Focus the name field
    setTimeout(() => {
      document.getElementById('project-name').focus();
    }, 100);
  }

  // Utility method for other modules to access app instance
  static getInstance() {
    return window.app;
  }

  /**
   * Initialize Google OAuth + Calendar services when feature flag is enabled
   */
  initializeGoogleIntegrations() {
    const flagEnabled = this.core.featureFlags.isEnabled('googleCalendarRead');

    if (!flagEnabled) {
      this.oauthClient = null;
      this.calendarService = null;
      this.clearGoogleCalendarEvents();
      this.updateIntegrationState('calendar', {
        status: 'disabled',
        connected: false,
        error: null,
        lastSync: null,
      });
      return;
    }

    const clientId = this.resolveGoogleClientId();
    if (!clientId) {
      this.updateIntegrationState('calendar', {
        status: 'pending_configuration',
        connected: false,
        error: 'Missing Google OAuth client ID',
      });
      console.warn('[LifeDashboard] Google Calendar flag enabled but client ID is missing');
      return;
    }

    if (!this.oauthClient) {
      try {
        this.oauthClient = new GoogleOAuthClient({
          clientId,
          redirectUri: this.resolveOAuthRedirectUri(),
          scopes: GoogleCalendarService.DEFAULT_SCOPES,
          storage: typeof window !== 'undefined' ? window.localStorage : undefined,
        });
      } catch (error) {
        console.error('[LifeDashboard] Failed to create Google OAuth client', error);
        this.updateIntegrationState('calendar', {
          status: 'error',
          connected: false,
          error: error.message,
        });
        return;
      }
    }

    this.updateIntegrationState('calendar', {
      status: 'initializing',
      connected: false,
      error: null,
    });

    this.oauthClient
      .initialize()
      .then(() => {
        this.calendarService = new GoogleCalendarService({
          oauthClient: this.oauthClient,
          featureFlags: this.featureFlags,
        });

        const writeGranted = this.oauthClient?.hasScopes?.(GoogleCalendarService.WRITE_SCOPES) ?? false;
        this.enableCalendarWriteConsent(writeGranted, { silent: true });

        const hasToken = Boolean(this.oauthClient.tokens?.accessToken);
        if (hasToken) {
          this.updateIntegrationState('calendar', {
            status: 'connected',
            connected: true,
            error: null,
            lastSync: this.calendarService?.lastSync ?? null,
          });
          this.refreshGoogleCalendarEvents({ silent: true });
        } else {
          this.clearGoogleCalendarEvents();
          this.updateIntegrationState('calendar', {
            status: 'ready',
            connected: false,
            error: null,
          });
        }
      })
      .catch((error) => {
        console.error('[LifeDashboard] Google OAuth initialization failed', error);
        this.updateIntegrationState('calendar', {
          status: 'error',
          connected: false,
          error: error.message,
        });
      });
  }

  handleGoogleCalendarAction() {
    const integration = this.data.get('integrationState', {});
    const calendarState = integration.calendar || {};
    const flagEnabled = this.featureFlags.isEnabled('googleCalendarRead');

    if (!flagEnabled) {
      this.featureFlags.set('googleCalendarRead', true);
      this.showNotification('📅 Google Calendar enabled. Preparing connection…');
      this.initializeGoogleIntegrations();
      this.track('calendar_feature_enable');
      return;
    }

    if (this.isCalendarBusyStatus(calendarState.status)) {
      this.track('calendar_action_blocked', { status: calendarState.status });
      return;
    }

    if (calendarState.connected) {
      const confirmed = window.confirm(
        'Disconnect Google Calendar? Google events will be removed from the dashboard until you reconnect.'
      );
      if (confirmed) {
        this.track('calendar_disconnect_confirmed');
        this.disconnectGoogleCalendar();
      }
      return;
    }

    if (calendarState.status === 'pending_configuration') {
      this.showNotification('Add a Google OAuth client ID to connect Google Calendar.');
      this.track('calendar_missing_client_id');
      return;
    }

    if (!this.oauthClient) {
      this.initializeGoogleIntegrations();
    }

    this.startGoogleCalendarOAuth();
  }

  handleGoogleCalendarWriteAction() {
    const integration = this.data.get('integrationState', {});
    const calendarState = integration.calendar || {};

    if (!calendarState.connected) {
      this.showNotification('Connect Google Calendar first to enable write access.');
      this.track('calendar_write_action_blocked', { reason: 'not_connected' });
      return;
    }

    if (this.isCalendarBusyStatus(calendarState.status)) {
      this.track('calendar_write_action_blocked', { reason: calendarState.status });
      return;
    }

    const consent = this.getCalendarWriteConsent();
    if (consent.enabled) {
      this.enableCalendarWriteConsent(false);
      this.track('calendar_write_disabled');
      return;
    }

    this.track('calendar_write_request');
    this.requestCalendarWriteAccess();
  }

  async requestCalendarWriteAccess() {
    if (!this.oauthClient) {
      this.initializeGoogleIntegrations();
      if (!this.oauthClient) {
        this.showNotification('Calendar integration is still loading. Try again in a moment.');
        return;
      }
    }

    const requiredScopes = [
      ...GoogleCalendarService.DEFAULT_SCOPES,
      ...GoogleCalendarService.WRITE_SCOPES,
    ];

    if (this.oauthClient.hasScopes?.(requiredScopes)) {
      this.enableCalendarWriteConsent(true);
      this.showNotification('Calendar write access is already enabled.');
      this.updateSettingsStates();
      return;
    }

    this.showNotification('Requesting Google Calendar write access…');
    this.startGoogleCalendarOAuth({
      scopes: requiredScopes,
      reason: 'write_upgrade',
    });
  }

  enableCalendarWriteConsent(enabled, options = {}) {
    const current = this.getCalendarWriteConsent();
    const { silent = false } = options;

    if (enabled) {
      if (!current.enabled) {
        this.data.set('calendarWriteConsent', {
          enabled: true,
          grantedAt: new Date().toISOString(),
        });
        if (!silent) {
          this.showNotification('✅ Calendar write access enabled');
        }
      }
      return;
    }

    if (current.enabled || current.grantedAt) {
      this.data.set('calendarWriteConsent', {
        enabled: false,
        grantedAt: null,
      });
      if (!silent) {
        this.showNotification('Google Calendar write access disabled. Staying read-only.');
      }
    }
  }

  isCalendarBusyStatus(status) {
    return ['initializing', 'authorizing', 'exchanging'].includes(status);
  }

  async startGoogleCalendarOAuth({ scopes = null, reason = 'read' } = {}) {
    if (!this.oauthClient) {
      this.showNotification('Google Calendar is still initializing. Please try again in a moment.');
      return;
    }

    try {
      if (Array.isArray(scopes) && scopes.length) {
        this.oauthClient.setScopes(scopes);
      } else {
        this.oauthClient.resetScopesToDefault();
      }

      this.updateIntegrationState('calendar', {
        status: 'authorizing',
        error: null,
      });
      this.track('calendar_auth_start', { reason });

      const authUrl = await this.oauthClient.startAuthorization();
      const width = 480;
      const height = 720;
      const dualScreenLeft = window.screenLeft ?? window.screenX ?? 0;
      const dualScreenTop = window.screenTop ?? window.screenY ?? 0;
      const screenWidth = window.innerWidth ?? document.documentElement.clientWidth ?? screen.width;
      const screenHeight = window.innerHeight ?? document.documentElement.clientHeight ?? screen.height;
      const left = dualScreenLeft + Math.max(0, (screenWidth - width) / 2);
      const top = dualScreenTop + Math.max(0, (screenHeight - height) / 2);

      this.oauthPopup = window.open(
        authUrl,
        'life-dashboard-google-oauth',
        `scrollbars=yes,width=${width},height=${height},top=${top},left=${left}`
      );

      if (!this.oauthPopup) {
        this.updateIntegrationState('calendar', {
          status: 'ready',
        });
        this.showNotification('Please allow popups to connect Google Calendar.');
        this.track('calendar_auth_popup_blocked');
        return;
      }

      this.oauthPopup.focus();
    } catch (error) {
      console.error('[LifeDashboard] Failed to start Google OAuth flow', error);
      this.updateIntegrationState('calendar', {
        status: 'error',
        connected: false,
        error: error.message,
      });
      this.showNotification('⚠️ Could not open Google Calendar authorization.');
      this.track('calendar_auth_start_error', { status: 'failed' });
    }
  }

  handleOAuthWindowMessage(event) {
    if (!event?.data || event.origin !== window.location.origin) {
      return;
    }

    const { type, code, error, state } = event.data;
    if (type !== 'life-dashboard-oauth') {
      return;
    }

    if (this.oauthPopup && !this.oauthPopup.closed) {
      this.oauthPopup.close();
    }
    this.oauthPopup = null;

    if (error) {
      this.updateIntegrationState('calendar', {
        status: 'error',
        connected: false,
        error,
      });
      this.showNotification('⚠️ Google Calendar authorization was cancelled.');
      this.track('calendar_auth_cancelled', { status: error });
      return;
    }

    if (code) {
      this.completeGoogleOAuth(code, state);
    }
  }

  async completeGoogleOAuth(code, state) {
    if (!this.oauthClient) {
      this.initializeGoogleIntegrations();
      if (!this.oauthClient) {
        return;
      }
    }

    if (state && this.oauthClient.state && state !== this.oauthClient.state) {
      console.warn('[LifeDashboard] OAuth state mismatch. Ignoring response.');
      this.updateIntegrationState('calendar', {
        status: 'error',
        connected: false,
        error: 'Authorization state mismatch',
      });
      return;
    }

    try {
      this.updateIntegrationState('calendar', {
        status: 'exchanging',
        error: null,
      });

      await this.oauthClient.exchangeCodeForTokens(code);

      const writeGranted = this.oauthClient.hasScopes?.(GoogleCalendarService.WRITE_SCOPES) ?? false;
      this.enableCalendarWriteConsent(writeGranted, { silent: true });
      if (writeGranted) {
        this.track('calendar_write_scope_granted');
      }

      this.updateIntegrationState('calendar', {
        status: 'connected',
        connected: true,
        error: null,
      });

      await this.refreshGoogleCalendarEvents({ silent: false });
      this.showNotification('📅 Google Calendar connected');
      this.track('calendar_auth_success');
    } catch (error) {
      console.error('[LifeDashboard] Failed to complete Google OAuth exchange', error);
      this.updateIntegrationState('calendar', {
        status: 'error',
        connected: false,
        error: error.message,
      });
      this.showNotification('⚠️ Unable to complete Google Calendar connection.');
      this.track('calendar_auth_exchange_error', { status: error.message });
    }
  }

  disconnectGoogleCalendar() {
    try {
      this.oauthClient?.clearTokens?.();
    } catch (error) {
      console.warn('[LifeDashboard] Failed to clear stored Google tokens', error);
    }

    this.clearGoogleCalendarEvents();
    this.updateIntegrationState('calendar', {
      status: 'ready',
      connected: false,
      error: null,
      lastSync: null,
    });
    this.enableCalendarWriteConsent(false, { silent: true });
    this.showNotification('📅 Google Calendar disconnected');
    this.track('calendar_disconnect');
  }

  async refreshGoogleCalendarEvents({ silent = false } = {}) {
    if (!this.calendarService || !this.featureFlags.isEnabled('googleCalendarRead')) {
      return;
    }

    try {
      const now = new Date();
      const timeMax = new Date(now);
      timeMax.setDate(now.getDate() + 7);

      const result = await this.calendarService.fetchUpcomingEvents({
        timeMin: now,
        timeMax,
      });

      if (result.status === 'ok') {
        const googleEvents = result.events
          .map((event) => this.transformGoogleEvent(event))
          .filter((event) => Boolean(event?.start));

        const existingEvents = this.data.get('calendar_events', []);
        const otherEvents = Array.isArray(existingEvents)
          ? existingEvents.filter((event) => event?.provider !== 'google')
          : [];
        const mergedEvents = [...otherEvents, ...googleEvents];
        this.data.set('calendar_events', mergedEvents);

        if (this.calendarService) {
          this.calendarService.lastSync = result.syncedAt || new Date().toISOString();
        }

        this.updateIntegrationState('calendar', {
          status: 'connected',
          connected: true,
          error: null,
          lastSync: result.syncedAt || new Date().toISOString(),
        });

        if (!silent) {
          this.showNotification('📅 Google Calendar synced');
        }
        this.track('calendar_sync_ok', { count: googleEvents.length });
      } else if (result.status === 'unauthenticated') {
        this.clearGoogleCalendarEvents();
        this.updateIntegrationState('calendar', {
          status: 'ready',
          connected: false,
        });
        this.enableCalendarWriteConsent(false, { silent: true });
        this.track('calendar_sync_unauthenticated');
      } else if (result.status === 'disabled') {
        this.clearGoogleCalendarEvents();
        this.updateIntegrationState('calendar', {
          status: 'disabled',
          connected: false,
        });
        this.enableCalendarWriteConsent(false, { silent: true });
        this.track('calendar_sync_disabled');
      } else if (result.status === 'error') {
        this.clearGoogleCalendarEvents();
        this.updateIntegrationState('calendar', {
          status: 'error',
          connected: false,
          error: result.error?.message || 'Unable to sync calendar',
        });
        if (!silent) {
          this.showNotification('⚠️ Google Calendar sync failed');
        }
        this.track('calendar_sync_error', { status: result.error?.message || 'unknown' });
      } else if (result.status === 'network_error') {
        this.updateIntegrationState('calendar', {
          status: 'error',
          connected: true,
          error: result.error?.message || 'Network error',
        });
        if (!silent) {
          this.showNotification('⚠️ Google Calendar is unreachable right now. We’ll retry shortly.');
        }
        this.track('calendar_sync_network_error', {
          message: result.error?.message || 'network_error',
        });
      }
    } catch (error) {
      console.error('[LifeDashboard] Google Calendar sync failed', error);
      this.clearGoogleCalendarEvents();
      this.updateIntegrationState('calendar', {
        status: 'error',
        connected: false,
        error: error.message,
      });
      if (!silent) {
        this.showNotification('⚠️ Google Calendar sync failed');
      }
       this.track('calendar_sync_exception', { status: error.message });
    }
  }

  transformGoogleEvent(event) {
    if (!event) return null;

    const toISO = (value) => {
      if (!value) return null;
      if (value instanceof Date) return value.toISOString();
      if (typeof value === 'string') return value;
      return null;
    };

    const start = toISO(event.start) || event.raw?.start?.dateTime || event.raw?.start?.date || null;
    const end = toISO(event.end) || event.raw?.end?.dateTime || event.raw?.end?.date || null;

    return {
      id: event.id ? `google-${event.id}` : `google-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      title: event.summary || '(No title)',
      start,
      end,
      provider: 'google',
      priority: 'medium',
      allDay: Boolean(event.isAllDay),
      location: event.location || '',
      sourceCalendar: event.calendarId || 'primary',
    };
  }

  clearGoogleCalendarEvents() {
    const events = this.core.data.get('calendar_events', []);
    if (!Array.isArray(events) || events.length === 0) {
      return;
    }

    const filtered = events.filter((event) => event?.provider !== 'google');
    if (filtered.length !== events.length) {
      this.core.data.set('calendar_events', filtered);
    }
  }

  updateIntegrationState(namespace, patch = {}) {
    const current = this.core.data.get('integrationState', {});
    const namespaceState = current[namespace] || {};
    const next = {
      ...current,
      [namespace]: {
        ...namespaceState,
        ...patch,
      },
    };
    this.core.data.set('integrationState', next);
  }

  resolveGoogleClientId() {
    try {
      if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_GOOGLE_CLIENT_ID) {
        return import.meta.env.VITE_GOOGLE_CLIENT_ID;
      }
    } catch (error) {
      // Ignore env lookup errors
    }

    if (typeof window !== 'undefined' && window.__GOOGLE_CLIENT_ID__) {
      return window.__GOOGLE_CLIENT_ID__;
    }

    return '';
  }

  resolveOAuthRedirectUri() {
    try {
      if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_GOOGLE_REDIRECT_URI) {
        return import.meta.env.VITE_GOOGLE_REDIRECT_URI;
      }
    } catch (error) {
      // Ignore env lookup errors
    }

    if (typeof window !== 'undefined' && window.__GOOGLE_REDIRECT_URI__) {
      return window.__GOOGLE_REDIRECT_URI__;
    }

    if (typeof window !== 'undefined' && window.location?.origin) {
      return `${window.location.origin.replace(/\/$/, '')}/oauth-callback.html`;
    }
    return '/oauth-callback.html';
  }

  getCalendarService() {
    return this.calendarService;
  }

  /**
   * Initialize responsive layout controller for CSS-driven breakpoints
   */
  initializeResponsiveLayout() {
    if (!this.core.featureFlags.isEnabled('responsiveLayout')) {
      console.info('[LifeDashboard] Responsive layout flag disabled; skipping controller setup');
      return;
    }

    this.layoutController = new ResponsiveLayoutController({
      onChange: (layout) => this.handleLayoutChange(layout),
    });
  }

  /**
   * Respond to layout tier changes
   * @param {{tier: string, width: number}} layout
   */
  handleLayoutChange(layout) {
    this.layoutState = {
      tier: layout.tier,
      width: layout.width,
    };
  }

  /**
   * Provide current layout state to other modules
   * @returns {{tier: string, width: number}}
   */
  getLayoutState() {
    return { ...this.layoutState };
  }
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    // On localhost, force-reload the main CSS if it failed to parse (cache fallback)
    try {
      const isLocal = ['localhost', '127.0.0.1'].includes(location.hostname);
      if (isLocal) {
        const reloadIfStale = () => {
          const link = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
            .find(l => /assets\/main-.*\.css/.test(l.getAttribute('href') || ''));
          if (!link) return;
          const sheet = Array.from(document.styleSheets)
            .find(s => (s.href || '').includes('assets/main-'));
          let rulesCount = 0;
          try { rulesCount = sheet?.cssRules?.length ?? 0; } catch (e) { rulesCount = -1; }
          // If stylesheet failed to load/parse, bust cache with a timestamp
          if (!sheet || rulesCount <= 0) {
            const base = link.href.split('?')[0];
            link.href = `${base}?ts=${Date.now()}`;
          }
        };
        // Try shortly after DOM ready and again on load
        setTimeout(reloadIfStale, 50);
        window.addEventListener('load', () => setTimeout(reloadIfStale, 50));
      }
    } catch {}
    window.app = new LifeDashboard();
  });
} else {
  try {
    const isLocal = ['localhost', '127.0.0.1'].includes(location.hostname);
    if (isLocal) {
      const link = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
        .find(l => /assets\/main-.*\.css/.test(l.getAttribute('href') || ''));
      if (link) {
        const sheet = Array.from(document.styleSheets)
          .find(s => (s.href || '').includes('assets/main-'));
        let rulesCount = 0;
        try { rulesCount = sheet?.cssRules?.length ?? 0; } catch (e) { rulesCount = -1; }
        if (!sheet || rulesCount <= 0) {
          const base = link.href.split('?')[0];
          link.href = `${base}?ts=${Date.now()}`;
        }
      }
    }
  } catch {}
  window.app = new LifeDashboard();
}

// Register SW only when explicitly enabled and in production
const ENABLE_SW = String(import.meta?.env?.VITE_ENABLE_SW || '').toLowerCase() === 'true';
if (ENABLE_SW && import.meta?.env?.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    // Skip file:// to avoid scope errors when previewing raw files
    if (!/^https?:$/.test(window.location.protocol)) return;

    navigator.serviceWorker
      .register('service-worker.js')
      .then((registration) => {
        console.log('Service worker registered:', registration.scope);
      })
      .catch((error) => {
        console.error('Service worker registration failed:', error);
      });
  });
}

// In dev/preview, proactively unregister any existing service workers and clear old caches.
// This prevents stale CSS/HTML from previous builds controlling localhost.
if (!ENABLE_SW && 'serviceWorker' in navigator) {
  window.addEventListener('load', async () => {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      for (const r of regs) {
        try { await r.unregister(); } catch {}
      }
      if (window.caches?.keys) {
        const keys = await caches.keys();
        for (const key of keys) {
          try { await caches.delete(key); } catch {}
        }
      }
      // Reload once to ensure a clean, non-controlled page
      if (regs.length) {
        window.location.reload();
      }
    } catch (e) {
      console.warn('SW cleanup skipped:', e);
    }
  });
}
